/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useMemo, useState, useEffect, useRef } from "react";
import { useWBL } from "../../context/WBLContext";
import { formatPhoneNumber } from "../../utils/phone";
import { formatTime12HourEST } from "../../utils/timeUtils";
import {
  Briefcase,
  User,
  Users,
  Clipboard,
  ClipboardCheck,
  Check,
  ShieldCheck,
  X,
  ChevronDown,
  ChevronUp,
  ChevronRight,
  ChevronLeft,
  Calendar,
  ExternalLink,
  Clock,
  FileText,
  CheckCircle,
  XCircle,
  Send,
  Award,
  BookOpen,
  Eye,
  AlertCircle,
  Sparkles,
  Download,
  Search,
  Mail,
  Filter,
  LayoutList,
  LayoutGrid,
  ArrowUpDown,
  GraduationCap,
  Building2,
  Phone,
  RotateCcw,
} from "lucide-react";
import { StudentAvatar, isUrl } from "../Common/StudentAvatar";
import { ProfessionalOrgFallback } from "../Common/ProfessionalOrgFallback";
import { HeroBanner } from "../Common/HeroBanner";
import { SearchBar } from "../Common/SearchBar";
import { VerifiedAddressInput } from "../Common/VerifiedAddressInput";
import { useInAppDialog } from "../Common/InAppDialog";
import { Application, UserProfile, TrainingAgreement } from "../../types";
import { formatLastNameFirst, sortStudentsByLastName } from "../../utils/studentUtils";
import { db, isUsingMockKeys } from "../../firebase";
import {
  collection,
  onSnapshot,
  query,
  where,
  documentId,
  doc,
  getDocs,
} from "firebase/firestore";

const CandidateProfile: React.FC<{
  studentId: string;
  studentName: string;
  studentEmail?: string;
}> = ({ studentId, studentName, studentEmail }) => {
  const { users } = useWBL()
  const [localUser, setLocalUser] = useState<UserProfile | null>(null)

  useEffect(() => {
    if (!studentId || isUsingMockKeys) return;
    const contextUser = (users || []).find((u) => u.uid === studentId)
    if (contextUser && contextUser.avatar) return;

    const unsub = onSnapshot(
      doc(db, "users", studentId),
      (snap) => {
        if (snap.exists()) {
          setLocalUser(snap.data() as UserProfile)
        }
      },
      (error) => {
        console.warn("CandidateProfile snap error:", error.message)
      },
    )
    return () => unsub()
  }, [studentId, users])

  const profile = (users || []).find((u) => u.uid === studentId) || localUser;

  return (
    <div className="space-y-4">
      {/* PROFESSIONAL SUMMARY */}
      <div className="bg-slate-50/60 border border-slate-200 rounded-xl p-4 space-y-2">
        <h4 className="text-[9px] font-black text-slate-500 uppercase tracking-widest font-mono">
          PROFESSIONAL SUMMARY
        </h4>
        <div className="bg-white p-3.5 rounded-lg border border-slate-100 text-xs text-slate-600 leading-relaxed">
          {profile?.description ? (
            <div 
              className="prose prose-sm max-w-none text-slate-600 [&_a]:text-[#0094c8] [&_a]:underline hover:[&_a]:text-[#0077a3] [&_a]:font-medium [&_a]:cursor-pointer"
              dangerouslySetInnerHTML={{ __html: profile.description }}
            />
          ) : (
            <p className="italic text-slate-400">No professional summary provided by candidate.</p>
          )}
        </div>
      </div>

      {/* CANDIDATE CONTACT */}
      <div className="bg-slate-50/60 border border-slate-200 rounded-xl p-4 space-y-2">
        <h4 className="text-[9px] font-black text-slate-500 uppercase tracking-widest font-mono">
          CANDIDATE CONTACT
        </h4>
        <div className="bg-white p-3 rounded-lg border border-slate-100 space-y-3">
          <div className="flex items-center space-x-3 pb-2.5 border-b border-slate-100">
            <div className="w-10 h-10 rounded-full overflow-hidden border border-slate-200 bg-slate-50 shadow-sm shrink-0">
              <StudentAvatar
                studentId={studentId}
                studentName={studentName}
                studentEmail={studentEmail}
                profile={profile}
              />
            </div>
            <div>
              <p className="text-xs font-bold text-slate-800">
                {formatLastNameFirst(studentName || profile?.name || "Student Candidate", profile)}
              </p>
              <p className="text-[10px] text-slate-400 uppercase">
                {studentEmail || profile?.email}
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-[10px] font-mono uppercase text-slate-500">
            {profile?.school && (
              <div>
                <span className="text-slate-400 font-bold block">School</span>
                <span className="text-slate-700 font-medium normal-case">{profile.school}</span>
              </div>
            )}
            {profile?.contactPhone && (
              <div>
                <span className="text-slate-400 font-bold block">Phone</span>
                <span className="text-slate-700 font-medium">{profile.contactPhone}</span>
              </div>
            )}
            {profile?.classYear && (
              <div>
                <span className="text-slate-400 font-bold block">Class Year</span>
                <span className="text-slate-700 font-medium">{profile.classYear}</span>
              </div>
            )}
            {profile?.academicLevel && (
              <div>
                <span className="text-slate-400 font-bold block">Academic Level</span>
                <span className="text-slate-700 font-medium">{profile.academicLevel}</span>
              </div>
            )}
          </div>
        </div>
      </div>


    </div>
  );
};
const PositionDetail: React.FC<{ text: string }> = ({ text }) => {
  if (!text) {
    return <p className="text-sm text-slate-400 italic">No description provided for this opportunity.</p>;
  }
  const isHtml = /<[a-z][\s\S]*>/i.test(text);
  if (isHtml) {
    return (
      <div
        className="prose prose-sm max-w-none text-slate-600 [&_a]:text-[#0094c8] [&_a]:underline hover:[&_a]:text-[#0077a3] [&_a]:font-medium [&_a]:cursor-pointer [&_p]:mb-3 leading-relaxed"
        dangerouslySetInnerHTML={{ __html: text }}
      />
    );
  }
  return (
    <div className="text-sm text-slate-600 whitespace-pre-line leading-relaxed">
      {text}
    </div>
  );
};

const renderCoverLetterOrQA = (rawText: string) => {
  if (!rawText || !rawText.trim()) {
    return <p className="text-xs text-slate-400 italic">No supplemental disclosures or cover letter attached.</p>;
  }

  if (rawText.includes("Question:") && rawText.includes("Answer:")) {
    const blocks = rawText.split(/(?=Question:\s*)/g).filter((b) => b.trim());
    return (
      <div className="space-y-4">
        {blocks.map((block, idx) => {
          const answerIndex = block.indexOf("Answer:");
          let question = block;
          let answer = "";
          if (answerIndex !== -1) {
            question = block.substring(0, answerIndex).replace(/^Question:\s*/i, "").trim();
            answer = block.substring(answerIndex).replace(/^Answer:\s*/i, "").trim();
          } else {
            question = block.replace(/^Question:\s*/i, "").trim();
          }

          return (
            <div key={idx} className="bg-slate-50/70 border border-slate-200/80 rounded-2xl p-4.5 space-y-2">
              <div className="flex items-start gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-[#0094c8] mt-1.5 shrink-0" />
                <h5 className="text-xs font-bold text-[#0f2d4a] leading-snug">
                  {question || `Supplemental Question ${idx + 1}`}
                </h5>
              </div>
              <div className="pl-3.5 border-l-2 border-[#0094c8]/20 text-xs text-slate-600 whitespace-pre-line leading-relaxed">
                {answer || <span className="italic text-slate-400">No response recorded</span>}
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div className="bg-slate-50/60 border border-slate-200 rounded-2xl p-5 text-xs text-slate-700 whitespace-pre-line leading-relaxed">
      {rawText}
    </div>
  );
};

const StudentApplications: React.FC = () => {
  const {
    activeUser,
    activeTenant,
    applications,
    interviews,
    opportunities,
    users,
    setCurrentView,
    reviewApplication,
    scheduleInterview,
    cancelInterview,
    rescheduleInterview,
    placements,
    schools,
    allSchools,
    viewingStudentId,
    selectedTerm,
    tasks,
    recommendations,
  } = useWBL()

  const { showAlert, showConfirm, showToast } = useInAppDialog()

  const userRole = activeUser?.role || "student";
  const canReview =
    userRole === "organization" ||
    userRole === "regional_admin" ||
    userRole === "superadmin";
  const userUid =
    viewingStudentId ||
    (userRole === "parent" ? activeUser?.childId : activeUser?.uid)
  const orgName = activeUser?.orgName || "";
  const [onboardingModal, setOnboardingModal] = useState<{
    show: boolean;
    content: string | null;
  }>({ show: false, content: null });
  const [interviewModal, setInterviewModal] = useState<{
    show: boolean;
    app: Application | null;
  }>({ show: false, app: null });
  const [interviewData, setInterviewData] = useState({
    date: "",
    time: "",
    person: "",
    mode: "virtual",
    link: "",
    address: "",
    phone: "",
  })

  const [acceptingAppId, setAcceptingAppId] = useState<string | null>(null)
  const [agreementForm, setAgreementForm] = useState<TrainingAgreement | null>(
    null,
  )
  const [decliningAppId, setDecliningAppId] = useState<string | null>(null)
  const [declineLetterText, setDeclineLetterText] = useState<string>("")
  const [searchQuery, setSearchQuery] = useState("")

  // Live database users fallback listener to bypass tenant-filtering for organizations in applications list
  const [dbUsers, setDbUsers] = useState<UserProfile[]>([])
  const [districts, setDistricts] = useState<{ id: string; name: string }[]>(
    [],
  )

  useEffect(() => {
    if (isUsingMockKeys) return;
    const unsubD = onSnapshot(collection(db, "districts"), (snap) => {
      setDistricts(snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) })))
    })
    return () => {
      unsubD()
    };
  }, [isUsingMockKeys])

  // Filter applications based on role and selected term
  const filteredApps = useMemo(() => {
    let baseApps = applications;

    // Filter by term based on the opportunities being posted as a selected term opportunity
    baseApps = baseApps.filter((a) => {
      const opp = opportunities.find((o) => o.id === a.opportunityId)
      if (opp) {
        return (
          opp.semester === selectedTerm ||
          opp.term === selectedTerm ||
          (!opp.semester && !opp.term)
        )
      }
      // Fallback if opportunity is not found
      return a.term === selectedTerm || !a.term;
    })

    let filtered = [];

    if (userRole === "organization") {
      // Organizations should see apps matching their org name regardless of which tenant perspective they are in
      // if those apps are assigned to their org.
      filtered = baseApps.filter((a) => {
        const matchesOrg =
          (a.organizationName || "").toLowerCase().trim() ===
          orgName.toLowerCase().trim()
        // Also allow local tenant matches for safety
        return (
          matchesOrg ||
          (a.tenantId === activeTenant.id && a.organizationName === orgName)
        )
      })
    } else if (userRole === "regional_admin") {
      filtered = baseApps.filter((a) => 
        activeTenant.id === 'global' ||
        a.tenantId === activeTenant.id || 
        a.postedByUserId === userUid || 
        (Boolean(a.postedByUserEmail) && a.postedByUserEmail?.toLowerCase() === activeUser?.email?.toLowerCase())
      );
    } else if (userRole === "advisor") {
      if (!activeUser?.verified) {
        return [];
      }
      const referenceIds = Array.from(new Set([
        activeUser?.schoolId,
        activeUser?.pendingSchoolId,
        ...(activeUser?.schoolsServedIds || []),
        activeUser?.school,
        activeUser?.pendingSchool,
        ...(activeUser?.schoolsServed || [])
      ].filter(Boolean))) as string[];

      const schoolPool = allSchools && allSchools.length > 0 ? allSchools : schools || [];
      const advisorSchools = schoolPool.filter(s => {
        return referenceIds.some(ref => {
          const normRef = ref.toLowerCase().trim()
          const normSchoolName = s.name.toLowerCase().trim()
          const normSchoolId = s.id.toLowerCase().trim()
          return normRef === normSchoolId || normRef === normSchoolName;
        })
      })
      const advisorSchoolIds = advisorSchools.map(s => s.id)
      const advisorSchoolNames = advisorSchools.map(s => s.name.toLowerCase().trim())

      const advisorStudents = (users || []).filter(u => {
        if (u.role !== 'student') return false;
        const ferpaGranted = typeof u.ferpaConsent === 'object'
          ? u.ferpaConsent?.granted === true
          : u.ferpaConsent === true;
        if (!ferpaGranted) return false;

        if (u.schoolId && advisorSchoolIds.includes(u.schoolId)) return true;
        if (u.school && advisorSchoolNames.includes(u.school.toLowerCase().trim())) return true;
        return false;
      })
      const studentIds = advisorStudents.map(s => s.uid)

      filtered = baseApps.filter((a) => studentIds.includes(a.studentId) && (activeTenant.id === 'global' || a.tenantId === activeTenant.id))
    } else if (userRole === "superadmin") {
      filtered = baseApps;
    } else {
      // Default to student view
      filtered = baseApps.filter((a) => a.studentId === userUid)
    }
    
    // Apply search query filter
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim()
      filtered = filtered.filter(a => 
        (a.studentName || "").toLowerCase().includes(q) ||
        (a.opportunityTitle || "").toLowerCase().includes(q) ||
        (a.organizationName || "").toLowerCase().includes(q) ||
        (a.studentEmail || "").toLowerCase().includes(q)
      )
    }
    
    return filtered.map((app) => ({
      ...app,
      interviewScheduled:
        app.interviewScheduled ||
        interviews.some((i) => i.applicationId === app.id),
    }))
  }, [
    applications,
    interviews,
    userUid,
    userRole,
    orgName,
    activeTenant.id,
    schools,
    users,
    selectedTerm,
    opportunities,
    activeUser,
    searchQuery,
  ])

  // Extract stable user keys to prevent snapshot infinite loop
  const studentIdsKey = useMemo(() => {
    return Array.from(new Set(filteredApps.map((a) => a.studentId).filter(Boolean))).sort().join(',')
  }, [filteredApps])

  const orgIdsKey = useMemo(() => {
    return Array.from(
      new Set(
        filteredApps
          .map((a) => {
            const op = opportunities.find((o) => o.id === a.opportunityId)
            return op?.organizationId;
          })
          .filter(Boolean),
      ),
    ).sort().join(',')
  }, [filteredApps, opportunities])

  const orgNamesKey = useMemo(() => {
    return Array.from(
      new Set(filteredApps.map((a) => a.organizationName).filter(Boolean)),
    ).sort().join(',')
  }, [filteredApps])

  useEffect(() => {
    if (isUsingMockKeys || (!studentIdsKey && !orgIdsKey && !orgNamesKey)) return;

    const studentIds = studentIdsKey ? studentIdsKey.split(',').filter(Boolean) : [];
    const orgIds = orgIdsKey ? orgIdsKey.split(',').filter(Boolean) : [];
    const allUserIds = Array.from(new Set([...studentIds, ...orgIds]));
    const orgNames = orgNamesKey ? orgNamesKey.split(',').filter(Boolean) : [];

    // Split allUserIds into chunks of 10 for Firestore 'in' query limit
    const chunks = [];
    if (allUserIds.length > 0) {
      for (let i = 0; i < allUserIds.length; i += 10) {
        chunks.push(allUserIds.slice(i, i + 10));
      }
    }

    const unsubscribes = chunks.map((chunk) => {
      const q = query(
        collection(db, "users"),
        where(documentId(), "in", chunk),
      )
      return onSnapshot(
        q,
        (snap) => {
          setDbUsers((prev) => {
            const newBatch = snap.docs.map((d) => d.data() as UserProfile)
            const combined = [...prev];
            newBatch.forEach((user) => {
              const index = combined.findIndex((u) => u.uid === user.uid)
              if (index === -1) {
                combined.push(user)
              } else {
                combined[index] = user;
              }
            })
            return combined;
          })
        },
        (error) => {
          console.warn("Scoped users snapshot error:", error.message)
        },
      )
    })

    // Also fetch by names for organizations to get their logos/profiles
    orgNames.forEach((name) => {
      const q1 = query(collection(db, "users"), where("orgName", "==", name))
      getDocs(q1)
        .then((snap) => {
          if (!snap.empty) {
            setDbUsers((prev) => {
              const newBatch = snap.docs.map((d) => d.data() as UserProfile)
              const combined = [...prev];
              newBatch.forEach((user) => {
                const index = combined.findIndex((u) => u.uid === user.uid)
                if (index === -1) combined.push(user)
                else combined[index] = user;
              })
              return combined;
            })
          }
        })
        .catch((err) => console.warn("Org orgName fetch error:", err))

      const q2 = query(collection(db, "users"), where("name", "==", name))
      getDocs(q2)
        .then((snap) => {
          if (!snap.empty) {
            setDbUsers((prev) => {
              const newBatch = snap.docs.map((d) => d.data() as UserProfile)
              const combined = [...prev];
              newBatch.forEach((user) => {
                const index = combined.findIndex((u) => u.uid === user.uid)
                if (index === -1) combined.push(user)
                else combined[index] = user;
              })
              return combined;
            })
          }
        })
        .catch((err) => console.warn("Org name fetch error:", err))
    })

    return () => unsubscribes.forEach((u) => u());
  }, [studentIdsKey, orgIdsKey, orgNamesKey, isUsingMockKeys])

  const mergedUsers = useMemo(() => {
    // Favor dbUsers (live unfiltered snapshots) as primary source
    const combined = [...dbUsers];
    // Add users from context only if not already present; ensures we have the latest live data
    users.forEach((u) => {
      const exists = combined.findIndex((c) => c.uid === u.uid)
      if (exists === -1) {
        combined.push(u)
      } else {
        // If it exists but context has newer/different data (unlikely but possible),
        // we can decide which to favor. dbUsers is usually more complete if permissions allow.
      }
    })
    return combined;
  }, [users, dbUsers])

  // Helper for initials
  const getInitials = (name: string) => {
    if (!name) return "?";
    return name
      .split(" ")
      .filter(Boolean)
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2)
  };

  // Track expanded state for each application id
  const [expandedAppIds, setExpandedAppIds] = useState<Record<string, boolean>>(
    {},
  )

  // Track selected opportunity for master-detail view (org role only)
  const [selectedOppId, setSelectedOppId] = useState<string | null>(null)

  // Track collapsed state for opportunities grouped view
  const [collapsedOpps, setCollapsedOpps] = useState<Record<string, boolean>>(
    {},
  )

  // Organization pipeline organization controls
  const [statusFilter, setStatusFilter] = useState<"all" | "pending" | "interview" | "accepted" | "rejected">("all")
  const [filterOpportunityId, setFilterOpportunityId] = useState<string>("all")
  const [viewMode, setViewMode] = useState<"card" | "table">("table")
  const [sortBy, setSortBy] = useState<"name_asc" | "name_desc" | "school" | "status" | "status_desc">("name_asc")
  const [statusHeaderDropdownOpen, setStatusHeaderDropdownOpen] = useState(false)
  const statusDropdownRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(event.target as Node)) {
        setStatusHeaderDropdownOpen(false)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  // Global KPI stats across all filtered applications
  const reviewStats = useMemo(() => {
    const list = filteredApps;
    const total = list.length;
    const pending = list.filter(
      (a) => a.status === "pending" || a.status === "submitted" || a.status === "under_review"
    ).length;
    const interview = list.filter(
      (a) => a.interviewScheduled || a.status === "reviewed" || a.status === "interviewing" || a.status === "scheduled"
    ).length;
    const accepted = list.filter(
      (a) => a.status === "accepted" || a.status === "placed" || a.status === "hired"
    ).length;
    const rejected = list.filter(
      (a) => a.status === "rejected" || a.status === "declined"
    ).length;
    return { total, pending, interview, accepted, rejected };
  }, [filteredApps]);

  // List of unique opportunities for quick dropdown switching
  const availableOpportunities = useMemo(() => {
    const oppMap = new Map<string, { id: string; title: string; count: number }>();
    filteredApps.forEach((app) => {
      const id = app.opportunityId || "unassigned";
      const opp = opportunities.find((o) => o.id === id);
      const title = opp?.title || app.opportunityTitle || "General Application";
      if (!oppMap.has(id)) {
        oppMap.set(id, { id, title, count: 1 });
      } else {
        oppMap.get(id)!.count++;
      }
    });
    return Array.from(oppMap.values());
  }, [filteredApps, opportunities]);

  // Filtered and sorted applications for organization review
  const organizedApps = useMemo(() => {
    let result = filteredApps;

    if (statusFilter === "pending") {
      result = result.filter(
        (a) => a.status === "pending" || a.status === "submitted" || a.status === "under_review"
      );
    } else if (statusFilter === "interview") {
      result = result.filter(
        (a) => a.interviewScheduled || a.status === "reviewed" || a.status === "interviewing" || a.status === "scheduled"
      );
    } else if (statusFilter === "accepted") {
      result = result.filter(
        (a) => a.status === "accepted" || a.status === "placed" || a.status === "hired"
      );
    } else if (statusFilter === "rejected") {
      result = result.filter(
        (a) => a.status === "rejected" || a.status === "declined"
      );
    }

    if (filterOpportunityId !== "all") {
      result = result.filter((a) => (a.opportunityId || "unassigned") === filterOpportunityId);
    }

    if (sortBy === "name_asc") {
      if (userRole === "student") {
        result = [...result].sort((a, b) =>
          (a.opportunityTitle || "").localeCompare(b.opportunityTitle || "")
        );
      } else {
        result = sortStudentsByLastName(
          result as any[],
          (a: any) => a.studentName,
          (a: any) => mergedUsers.find((u) => u.uid === a.studentId || u.email === a.studentEmail)
        );
      }
    } else if (sortBy === "name_desc") {
      if (userRole === "student") {
        result = [...result].sort((a, b) =>
          (b.opportunityTitle || "").localeCompare(a.opportunityTitle || "")
        );
      } else {
        result = sortStudentsByLastName(
          result as any[],
          (a: any) => a.studentName,
          (a: any) => mergedUsers.find((u) => u.uid === a.studentId || u.email === a.studentEmail)
        ).reverse();
      }
    } else if (sortBy === "school") {
      result = [...result].sort((a, b) => {
        const uA = mergedUsers.find((u) => u.uid === a.studentId || u.email === a.studentEmail);
        const uB = mergedUsers.find((u) => u.uid === b.studentId || u.email === b.studentEmail);
        const schoolA = schools.find((s) => s.id === uA?.school)?.name || uA?.school || "";
        const schoolB = schools.find((s) => s.id === uB?.school)?.name || uB?.school || "";
        return schoolA.localeCompare(schoolB);
      });
    } else if (sortBy === "status") {
      const priority: Record<string, number> = {
        pending: 1,
        submitted: 1,
        under_review: 1,
        reviewed: 2,
        interviewing: 2,
        scheduled: 2,
        accepted: 3,
        placed: 3,
        hired: 3,
        rejected: 4,
        declined: 4,
      };
      result = [...result].sort((a, b) => (priority[a.status] || 99) - (priority[b.status] || 99));
    } else if (sortBy === "status_desc") {
      const priority: Record<string, number> = {
        pending: 1,
        submitted: 1,
        under_review: 1,
        reviewed: 2,
        interviewing: 2,
        scheduled: 2,
        accepted: 3,
        placed: 3,
        hired: 3,
        rejected: 4,
        declined: 4,
      };
      result = [...result].sort((a, b) => (priority[b.status] || 99) - (priority[a.status] || 99));
    }

    return result;
  }, [filteredApps, statusFilter, filterOpportunityId, sortBy, mergedUsers, schools, userRole]);

  // Group applications by opportunity for organization, regional_admin, and superadmin view
  const groupedApps = useMemo<Record<string, Application[]> | null>(() => {
    if (
      userRole !== "organization" &&
      userRole !== "regional_admin" &&
      userRole !== "superadmin"
    )
      return null;
    const groups: Record<string, Application[]> = {};
    organizedApps.forEach((app) => {
      const key = app.opportunityId || "unassigned";
      if (!groups[key]) groups[key] = [];
      groups[key].push(app)
    })
    return groups;
  }, [organizedApps, userRole])

  // Set default selection
  useEffect(() => {
    if (userRole === "organization" && groupedApps && !selectedOppId) {
      const firstId = Object.keys(groupedApps)[0];
      if (firstId) setSelectedOppId(firstId)
    }
  }, [userRole, groupedApps, selectedOppId])

  const toggleAppDetails = (appId: string) => {
    setExpandedAppIds((prev) => ({
      ...prev,
      [appId]: !prev[appId],
    }))
  };

  const handleReviewSubmit = async (
    appId: string,
    status: "accepted" | "rejected",
    agreementData?: TrainingAgreement,
    declineLetter?: string,
  ) => {
    try {
      await reviewApplication(appId, status, agreementData, declineLetter)
    } catch (err: any) {
      console.error("Failed to review application:", err)
      showAlert({
        title: "Update Failed",
        message: "Failed to update application status: " + (err?.message || "Unknown error"),
        type: "error",
      })
    }
  };

  const getOverviewOnly = (description: string): string => {
    if (!description) return "";
    let cleanDesc = description;
    const responsibilitiesIndex = description.search(/\*\*Responsibilities:\*\*|Responsibilities:/i)
    if (responsibilitiesIndex !== -1) {
      cleanDesc = description.substring(0, responsibilitiesIndex).trim()
    } else {
      const keyRespIndex = description.search(/\*\*Key Responsibilities:\*\*|Key Responsibilities:/i)
      if (keyRespIndex !== -1) {
        cleanDesc = description.substring(0, keyRespIndex).trim()
      }
    }
    return cleanDesc
      .replace(/^\*\*Overview:\*\*/gi, '')
      .replace(/^Overview:/gi, '')
      .trim()
  };

  const handleReview = async (
    appId: string,
    status: "accepted" | "rejected",
  ) => {
    if (status === "accepted") {
      const targetApp = applications.find((a) => a.id === appId)
      const targetOpp = opportunities.find(
        (o) => o.id === targetApp?.opportunityId,
      )
      const studentProfile = users.find((u) => u.uid === targetApp?.studentId)
      const prefilledPathway = studentProfile?.careerCluster || targetOpp?.careerCluster || "Work-Based Learning";
      const cleanPrefilledPathway = prefilledPathway.replace(/\s*\([^)]*\)/g, '').trim()

      const activeOrgUser = users.find(
        (u) =>
          u.role === "organization" &&
          (u.uid === targetOpp?.organizationId || u.orgName === targetOpp?.organizationName)
      )

      const prefilledHours = targetOpp?.hoursRequired || (typeof targetOpp?.totalHours === 'number' ? targetOpp?.totalHours : parseInt(String(targetOpp?.totalHours || ''))) || 120;

      setAgreementForm({
        isAlignedToIGP: undefined,
        isPaid: targetOpp?.compensation && targetOpp.compensation.toLowerCase() === "unpaid" ? false : true,
        earnedCourseCredit: undefined,
        courseCode: "",
        hasInsurance: undefined,
        jobDescription: getOverviewOnly(
          targetOpp?.description || "We are seeking a highly motivated..."
        ),
        learningObjectives: targetOpp?.learningObjective
          ? targetOpp.learningObjective.split("\n").map(l => l.trim()).filter(Boolean)
          : [
              "1. Professionalism",
              "2. Communication",
              "3. Technical Competency",
            ],
        startDate: new Date().toISOString().split('T')[0],
        endDate: new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        assignedDays: ["Mon", "Tue", "Wed", "Thu", "Fri"],
        careerPathway: "",
        worksiteName: targetOpp?.organizationName || targetApp?.organizationName || "Spartanburg School District 8",
        assignedHoursTotal: prefilledHours,
        supervisorName: activeUser?.name || `${activeUser?.contactFirstName || ''} ${activeUser?.contactLastName || ''}`.trim() || activeOrgUser?.name || "Joshua Loso",
        supervisorPhone: activeUser?.contactPhone || activeUser?.phone || activeOrgUser?.contactPhone || "(864)630-6571",
        supervisorEmail: activeUser?.email || activeOrgUser?.email || "organization@wblportal.com"
      })
      setAcceptingAppId(appId)
    } else {
      const targetApp = applications.find((a) => a.id === appId)
      const text = `Dear ${targetApp?.studentName || "Student"},

Thank you for your interest in the "${targetApp?.opportunityTitle || "WBL Program"}" position. We sincerely appreciate the time and effort you put into your application and of sharing your career objectives with us.

After careful evaluation of all candidates, we regret to inform you that we will not be moving forward with your application for this specific opportunity. Our review process was highly competitive, and we had to make some exceptionally difficult selections.

We highly encourage you to continue looking at other open opportunities within our Work-Based Learning ecosystem. We wish you the absolute best with your academic progress and career pathway.

Sincerely,
${targetApp?.organizationName || "The Organization"} Recruitment Team`;

      setDeclineLetterText(text)
      setDecliningAppId(appId)
    }
  };

  const handleScheduleInterview = async () => {
    if (!interviewModal.app) return;
    try {
      const isRescheduling = interviewModal.app.interviewScheduled;
      const matchingInterview = isRescheduling
        ? interviews.find((i) => i.applicationId === interviewModal.app!.id)
        : null;

      const interviewPayload = {
        applicationId: interviewModal.app.id,
        opportunityId: interviewModal.app.opportunityId,
        opportunityTitle: interviewModal.app.opportunityTitle,
        tenantId: activeTenant.id,
        studentId: interviewModal.app.studentId,
        studentName: interviewModal.app.studentName,
        organizationName: interviewModal.app.organizationName,
        date: interviewData.date,
        time: interviewData.time,
        person: interviewData.person,
        mode: interviewData.mode as any,
        link: interviewData.mode === "virtual" ? interviewData.link : undefined,
        address:
          interviewData.mode === "in_person"
            ? interviewData.address
            : undefined,
        phone: interviewData.mode === "phone" ? interviewData.phone : undefined,
        status: "scheduled" as const,
      };

      if (isRescheduling && matchingInterview) {
        await rescheduleInterview(matchingInterview.id, interviewPayload)
        showToast({
          title: "Interview Rescheduled",
          message: "Interview rescheduled successfully.",
          type: "success",
        })
      } else {
        await scheduleInterview(interviewPayload)
        showToast({
          title: "Interview Scheduled",
          message: "Interview scheduled successfully.",
          type: "success",
        })
      }
      setInterviewModal({ show: false, app: null })
      setInterviewData({
        date: "",
        time: "",
        person: "",
        mode: "virtual",
        link: "",
        address: "",
        phone: "",
      })
    } catch (e: any) {
      console.error(e)
      showAlert({
        title: "Scheduling Failed",
        message: "Failed to schedule interview: " + (e?.message || "Please check details and retry"),
        type: "error",
      })
    }
  };

  const bannerProps = {
    title: "APPLICATIONS",
    subtitle: canReview
      ? (userRole === 'organization' ? "MONITOR YOUR RECRUITMENT PIPELINE PROGRESS" : "REVIEW AND SELECT STUDENTS FOR ACTIVE PLACEMENTS")
      : (userRole === 'advisor' ? "MONITOR AND TRACK ADVISEE APPLICATIONS & PLACEMENT PROGRESS" : "MONITOR YOUR WBL RECRUITMENT PIPELINE PROGRESS"),
    pageKey: "applications" as const,
  };

  if (acceptingAppId && agreementForm) {
    const targetApp = applications.find((a) => a.id === acceptingAppId)
    const daysOfWeek = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
    const currentDays = agreementForm.assignedDays || [];
    const toggleDay = (day: string) => {
      const updated = currentDays.includes(day)
        ? currentDays.filter((d) => d !== day)
        : [...currentDays, day];
      setAgreementForm({
        ...agreementForm,
        assignedDays: updated,
      })
    };

    return (
      <div className="w-[95%] mx-auto py-10 space-y-8 animate-fade-in">
        <div className="bg-white rounded-3xl border border-slate-200 shadow-xl overflow-hidden max-w-5xl mx-auto">
          <div className="bg-[#0094c8] p-8 text-white">
            <h2 className="text-2xl font-black uppercase tracking-tight font-sans">
              Setup Training Agreement
            </h2>
            <p className="text-blue-100 text-xs font-medium uppercase tracking-widest mt-1">
              Official Document Metadata for {targetApp?.studentName}
            </p>
          </div>

          <div className="p-8 space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Left Column: Logistics & Schedule */}
              <div className="space-y-5">
                <div className="space-y-2">
                  <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest font-mono">
                    WBL Alignment & Logistics
                  </label>
                  <div className="space-y-2">
                    {userRole !== "organization" && (
                      <div className="p-3 bg-slate-50 border border-slate-100 rounded-xl flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                        <span className="font-medium text-slate-700 text-xs flex-1">
                          Aligned to student's Individual Graduation Plan (IGP)?
                        </span>
                        <div className="flex items-center space-x-1 shrink-0">
                          <button
                            type="button"
                            onClick={() => setAgreementForm({ ...agreementForm, isAlignedToIGP: true })}
                            className={`px-3 py-1 rounded-lg font-black text-xs transition-all border ${
                              agreementForm.isAlignedToIGP === true
                                ? "bg-[#0094c8] border-[#0094c8] text-white"
                                : "bg-white border-slate-200 text-slate-500 hover:bg-slate-100"
                            }`}
                          >
                            YES
                          </button>
                          <button
                            type="button"
                            onClick={() => setAgreementForm({ ...agreementForm, isAlignedToIGP: false })}
                            className={`px-3 py-1 rounded-lg font-black text-xs transition-all border ${
                              agreementForm.isAlignedToIGP === false
                                ? "bg-slate-900 border-slate-900 text-white"
                                : "bg-white border-slate-200 text-slate-500 hover:bg-slate-100"
                            }`}
                          >
                            NO
                          </button>
                        </div>
                      </div>
                    )}

                    <div className="p-3 bg-slate-50 border border-slate-100 rounded-xl flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                      <span className="font-medium text-slate-700 text-xs flex-1">
                        Paid Placement
                      </span>
                      <div className="flex items-center space-x-1 shrink-0">
                        <button
                          type="button"
                          onClick={() => setAgreementForm({ ...agreementForm, isPaid: true })}
                          className={`px-3 py-1 rounded-lg font-black text-xs transition-all border ${
                            agreementForm.isPaid === true
                              ? "bg-[#0094c8] border-[#0094c8] text-white"
                              : "bg-white border-slate-200 text-slate-500 hover:bg-slate-100"
                          }`}
                        >
                          YES
                        </button>
                        <button
                          type="button"
                          onClick={() => setAgreementForm({ ...agreementForm, isPaid: false })}
                          className={`px-3 py-1 rounded-lg font-black text-xs transition-all border ${
                            agreementForm.isPaid === false
                              ? "bg-slate-900 border-slate-900 text-white"
                              : "bg-white border-slate-200 text-slate-500 hover:bg-slate-100"
                          }`}
                        >
                          NO
                        </button>
                      </div>
                    </div>

                    {userRole !== "organization" && (
                      <>
                        <div className="p-3 bg-slate-50 border border-slate-100 rounded-xl flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                          <span className="font-medium text-slate-700 text-xs flex-1">
                            Earned course credit in career pathway?
                          </span>
                          <div className="flex items-center space-x-1 shrink-0">
                            <button
                              type="button"
                              onClick={() => setAgreementForm({ ...agreementForm, earnedCourseCredit: true })}
                              className={`px-3 py-1 rounded-lg font-black text-xs transition-all border ${
                                agreementForm.earnedCourseCredit === true
                                  ? "bg-[#0094c8] border-[#0094c8] text-white"
                                  : "bg-white border-slate-200 text-slate-500 hover:bg-slate-100"
                              }`}
                            >
                              YES
                            </button>
                            <button
                              type="button"
                              onClick={() => setAgreementForm({ ...agreementForm, earnedCourseCredit: false, courseCode: '' })}
                              className={`px-3 py-1 rounded-lg font-black text-xs transition-all border ${
                                agreementForm.earnedCourseCredit === false
                                  ? "bg-slate-900 border-slate-900 text-white"
                                  : "bg-white border-slate-200 text-slate-500 hover:bg-slate-100"
                              }`}
                            >
                              NO
                            </button>
                          </div>
                        </div>

                        {agreementForm.earnedCourseCredit && (
                          <div className="pl-2 py-1 animate-fade-in">
                            <label className="block text-[9px] font-black text-slate-400 uppercase tracking-widest font-mono mb-1">
                              Course Code
                            </label>
                            <input
                              type="text"
                              value={agreementForm.courseCode || ""}
                              onChange={(e) =>
                                setAgreementForm({
                                  ...agreementForm,
                                  courseCode: e.target.value,
                                })
                              }
                              placeholder="e.g. CTE-8001"
                              className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-lg text-xs focus:ring-2 focus:ring-[#0094c8] text-slate-700 font-bold focus:outline-hidden"
                            />
                          </div>
                        )}

                        <div className="p-3 bg-slate-50 border border-slate-100 rounded-xl flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                          <span className="font-medium text-slate-700 text-xs flex-1">
                            School Insurance coverage provided?
                          </span>
                          <div className="flex items-center space-x-1 shrink-0">
                            <button
                              type="button"
                              onClick={() => setAgreementForm({ ...agreementForm, hasInsurance: true })}
                              className={`px-3 py-1 rounded-lg font-black text-xs transition-all border ${
                                agreementForm.hasInsurance === true
                                  ? "bg-[#0094c8] border-[#0094c8] text-white"
                                  : "bg-white border-slate-200 text-slate-500 hover:bg-slate-100"
                              }`}
                            >
                              YES
                            </button>
                            <button
                              type="button"
                              onClick={() => setAgreementForm({ ...agreementForm, hasInsurance: false })}
                              className={`px-3 py-1 rounded-lg font-black text-xs transition-all border ${
                                agreementForm.hasInsurance === false
                                  ? "bg-slate-900 border-slate-900 text-white"
                                  : "bg-white border-slate-200 text-slate-500 hover:bg-slate-100"
                              }`}
                            >
                              NO
                            </button>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest font-mono">
                    Working Days of the Week
                  </label>
                  <div className="flex flex-wrap gap-2 pt-1">
                    {daysOfWeek.map((day) => {
                      const active = currentDays.includes(day)
                      return (
                        <button
                          key={day}
                          type="button"
                          onClick={() => toggleDay(day)}
                          className={`px-3 py-1.5 rounded-xl text-xs font-bold uppercase tracking-wider border transition-all cursor-pointer ${
                            active
                              ? "bg-[#0094c8] text-white border-[#0094c8] shadow-xs"
                              : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
                          }`}
                        >
                          {day}
                        </button>
                      )
                    })}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 pt-1">
                  <div className="space-y-1.5">
                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest font-mono">
                      Start Date
                    </label>
                    <input
                      type="date"
                      value={agreementForm.startDate || ""}
                      onChange={(e) =>
                        setAgreementForm({
                          ...agreementForm,
                          startDate: e.target.value,
                        })
                      }
                      className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl text-xs focus:ring-2 focus:ring-[#0094c8] text-slate-700 font-bold focus:outline-hidden"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest font-mono">
                      End Date
                    </label>
                    <input
                      type="date"
                      value={agreementForm.endDate || ""}
                      onChange={(e) =>
                        setAgreementForm({
                          ...agreementForm,
                          endDate: e.target.value,
                        })
                      }
                      className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl text-xs focus:ring-2 focus:ring-[#0094c8] text-slate-700 font-bold focus:outline-hidden"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 pt-1">
                  <div className="space-y-1.5">
                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest font-mono">
                      Worksite Name
                    </label>
                    <input
                      type="text"
                      value={agreementForm.worksiteName || ""}
                      onChange={(e) =>
                        setAgreementForm({
                          ...agreementForm,
                          worksiteName: e.target.value,
                        })
                      }
                      placeholder="e.g. Spartanburg School District 8"
                      className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl text-xs focus:ring-2 focus:ring-[#0094c8] text-slate-700 font-bold focus:outline-hidden"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest font-mono">
                      Worksite Assigned Hours
                    </label>
                    <input
                      type="number"
                      value={agreementForm.assignedHoursTotal ?? ""}
                      onChange={(e) =>
                        setAgreementForm({
                          ...agreementForm,
                          assignedHoursTotal: parseInt(e.target.value) || 0,
                        })
                      }
                      placeholder="e.g. 120"
                      className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl text-xs focus:ring-2 focus:ring-[#0094c8] text-slate-700 font-bold focus:outline-hidden"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 pt-1">
                  <div className="space-y-1.5">
                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest font-mono">
                      Worksite Supervisor
                    </label>
                    <input
                      type="text"
                      value={agreementForm.supervisorName || ""}
                      onChange={(e) =>
                        setAgreementForm({
                          ...agreementForm,
                          supervisorName: e.target.value,
                        })
                      }
                      placeholder="e.g. Joshua Loso"
                      className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl text-xs focus:ring-2 focus:ring-[#0094c8] text-slate-700 font-bold focus:outline-hidden"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest font-mono">
                      Worksite Phone
                    </label>
                    <input
                      type="text"
                      value={agreementForm.supervisorPhone || ""}
                      onChange={(e) =>
                        setAgreementForm({
                          ...agreementForm,
                          supervisorPhone: formatPhoneNumber(e.target.value),
                        })
                      }
                      placeholder="e.g. (864) 630-6571"
                      className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl text-xs focus:ring-2 focus:ring-[#0094c8] text-slate-700 font-bold focus:outline-hidden"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 pt-1">
                  <div className="space-y-1.5">
                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest font-mono">
                      Worksite Email
                    </label>
                    <input
                      type="email"
                      value={agreementForm.supervisorEmail || ""}
                      onChange={(e) =>
                        setAgreementForm({
                          ...agreementForm,
                          supervisorEmail: e.target.value,
                        })
                      }
                      placeholder="e.g. organization@wblportal.com"
                      className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl text-xs focus:ring-2 focus:ring-[#0094c8] text-slate-700 font-bold focus:outline-hidden"
                    />
                  </div>
                  {userRole !== "organization" ? (
                    <div className="space-y-1.5">
                      <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest font-mono">
                        Student's Career Pathway
                      </label>
                      <input
                        type="text"
                        value={agreementForm.careerPathway || ""}
                        onChange={(e) =>
                          setAgreementForm({
                            ...agreementForm,
                            careerPathway: e.target.value,
                          })
                        }
                        placeholder="Enter student's career pathway (e.g. Work-Based Learning)"
                        className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl text-xs focus:ring-2 focus:ring-[#0094c8] text-slate-700 font-bold focus:outline-hidden"
                      />
                    </div>
                  ) : (
                    <div />
                  )}
                </div>
              </div>

              {/* Right Column: Job Description & Learning Objectives */}
              <div className="space-y-5">
                <div className="space-y-1.5">
                  <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest font-mono">
                    Job Description Overview
                  </label>
                  <textarea
                    value={agreementForm.jobDescription}
                    onChange={(e) =>
                      setAgreementForm({
                        ...agreementForm,
                        jobDescription: e.target.value,
                      })
                    }
                    className="w-full h-[200px] p-4 bg-slate-50 border border-slate-200 rounded-2xl text-xs leading-relaxed text-slate-700 font-sans focus:ring-2 focus:ring-[#0094c8] focus:outline-hidden resize-none"
                    placeholder="No job description overview found."
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest font-mono">
                    Key Learning Objectives
                  </label>
                  <textarea
                    value={agreementForm.learningObjectives.join("\n")}
                    onChange={(e) =>
                      setAgreementForm({
                        ...agreementForm,
                        learningObjectives: e.target.value.split("\n"),
                      })
                    }
                    className="w-full h-[180px] p-4 bg-slate-50 border border-slate-200 rounded-2xl text-xs leading-relaxed text-slate-700 font-sans focus:ring-2 focus:ring-[#0094c8] focus:outline-hidden resize-none"
                    placeholder="No learning objectives found."
                  />
                </div>
              </div>
            </div>

            <div className="pt-8 border-t border-slate-100 flex items-center justify-end space-x-4">
              <button
                onClick={() => {
                  setAcceptingAppId(null)
                  setAgreementForm(null)
                }}
                className="px-6 py-3 text-slate-500 font-black uppercase text-[10px] tracking-widest hover:text-slate-900 transition-all cursor-pointer"
              >
                Discard
              </button>
              <button
                onClick={async () => {
                  await handleReviewSubmit(
                    acceptingAppId,
                    "accepted",
                    agreementForm,
                  )
                  setAcceptingAppId(null)
                  setAgreementForm(null)
                  showAlert({
                    title: "Candidate Accepted",
                    message: "Student successfully accepted and training agreement saved!",
                    type: "success",
                  })
                }}
                className="px-8 py-3 bg-slate-900 text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-slate-800 transition-all shadow-lg cursor-pointer"
              >
                Save Training Agreement
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (decliningAppId && declineLetterText !== undefined) {
    const targetApp = applications.find((a) => a.id === decliningAppId)
    return (
      <div className="w-[95%] mx-auto py-10 space-y-8 animate-fade-in">
        <div className="bg-white rounded-3xl border border-slate-200 shadow-xl overflow-hidden max-w-4xl mx-auto">
          <div className="bg-rose-950 p-8 text-white">
            <h2 className="text-2xl font-black uppercase tracking-tight font-sans">
              Draft Decline Letter
            </h2>
            <p className="text-rose-100 text-xs font-medium uppercase tracking-widest mt-1">
              Official Decline Notice for {targetApp?.studentName}
            </p>
          </div>

          <div className="p-8 space-y-6">
            <div className="space-y-4">
              <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest font-mono">
                Customized Decline Message
              </label>
              <textarea
                value={declineLetterText}
                onChange={(e) => setDeclineLetterText(e.target.value)}
                className="w-full h-[320px] p-6 bg-slate-50 border border-slate-200 rounded-2xl text-xs focus:ring-2 focus:ring-rose-500 font-mono leading-relaxed text-slate-700"
                placeholder="Write your email/letter contents..."
              />
            </div>

            <div className="pt-8 border-t border-slate-100 flex items-center justify-end space-x-4">
              <button
                onClick={() => {
                  setDecliningAppId(null)
                  setDeclineLetterText("")
                }}
                className="px-6 py-3 text-slate-500 font-black uppercase text-[10px] tracking-widest hover:text-slate-900 transition-all cursor-pointer shadow-xs border border-slate-200 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  await handleReviewSubmit(
                    decliningAppId,
                    "rejected",
                    undefined,
                    declineLetterText,
                  )
                  setDecliningAppId(null)
                  setDeclineLetterText("")
                  showToast({
                    title: "Application Declined",
                    message: "Candidate declined and notification saved.",
                    type: "info",
                  })
                }}
                className="px-8 py-3 bg-rose-950 hover:bg-rose-900 text-white rounded-xl text-[10px] font-black uppercase tracking-widest transition-all shadow-lg cursor-pointer"
              >
                Send & Save Decline Letter
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <HeroBanner {...bannerProps} />

      <div className="w-[95%] mx-auto mt-6 space-y-4">
        
        {/* Filter & Toolbar Controls */}
        <div className="bg-white rounded-xl border border-slate-200/80 p-3.5 shadow-2xs">
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
            <div className="flex-1 w-full">
              <SearchBar 
                value={searchQuery} 
                onChange={setSearchQuery} 
                placeholder={userRole === "student" ? "Search applications by title, company, cluster..." : "Search candidates by name, school, email..."} 
              />
            </div>

            {/* Reset Filters */}
            {(statusFilter !== "all" || filterOpportunityId !== "all" || searchQuery) && (
              <button
                type="button"
                onClick={() => {
                  setStatusFilter("all");
                  setFilterOpportunityId("all");
                  setSearchQuery("");
                }}
                className="px-2.5 py-1.5 text-[10px] font-bold text-slate-500 hover:text-slate-800 bg-slate-50 hover:bg-slate-100 rounded-lg border border-slate-200 flex items-center gap-1 transition-all uppercase tracking-wider cursor-pointer self-start sm:self-center"
                title="Reset all filters"
              >
                <RotateCcw className="w-3 h-3" />
                <span>Reset Filters</span>
              </button>
            )}
          </div>
        </div>

        {/* Stack of Cards or Master-Detail */}
        <div className="w-full">
          {filteredApps.length === 0 && searchQuery ? (
            <div className="bg-white border border-slate-200 rounded-3xl p-12 text-center shadow-sm space-y-4">
              <div className="mx-auto w-12 h-12 bg-slate-50 text-slate-400 rounded-full flex items-center justify-center">
                <Search className="w-6 h-6" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-slate-900">No matching applications</h3>
                <p className="text-sm text-slate-500">Try adjusting your search query to find what you're looking for.</p>
              </div>
              <button 
                onClick={() => setSearchQuery('')}
                className="text-[#0094c8] text-xs font-black uppercase tracking-widest hover:underline"
              >
                Clear Search
              </button>
            </div>
          ) : filteredApps.length === 0 && !searchQuery ? (
            <div className="bg-white border border-slate-200 rounded-3xl p-12 text-center shadow-sm space-y-6 max-w-xl mx-auto">
              <div className="mx-auto w-16 h-16 bg-blue-50 text-blue-600 rounded-full flex items-center justify-center">
                <FileText className="w-8 h-8" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-slate-900">
                  No Applications Found
                </h3>
                <p className="text-sm text-slate-500 mt-2">
                  {userRole === "organization"
                    ? `No students have applied to opportunities for ${orgName} in the ${activeTenant.name} perspective yet.`
                    : `You haven't submitted any applications for WBL training programs or learning roles in the ${activeTenant.name} perspective.`}
                </p>
              </div>
              {userRole === "student" && (
                <button
                  onClick={() => setCurrentView("opportunities")}
                  className="px-6 py-3 bg-[#0094c8] hover:bg-[#007ba6] text-white text-sm font-bold rounded-xl shadow-md transition-all uppercase tracking-wide cursor-pointer"
                >
                  Explore Open Opportunities
                </button>
              )}
            </div>
          ) : canReview && groupedApps ? (
            organizedApps.length === 0 ? (
              <div className="bg-white border border-slate-200 rounded-2xl p-8 text-center shadow-2xs space-y-3">
                <div className="mx-auto w-10 h-10 bg-slate-100 text-slate-400 rounded-full flex items-center justify-center">
                  <Filter className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-slate-800">No candidates match current filter</h3>
                  <p className="text-xs text-slate-500 mt-0.5">
                    No candidates found in "{statusFilter.toUpperCase()}" stage for the selected criteria.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setStatusFilter("all");
                    setFilterOpportunityId("all");
                    setSearchQuery("");
                  }}
                  className="px-4 py-1.5 bg-[#0094c8] text-white text-xs font-bold rounded-lg uppercase tracking-wider hover:bg-[#007ba6] cursor-pointer inline-flex items-center gap-1.5"
                >
                  <RotateCcw className="w-3.5 h-3.5" />
                  <span>View All Candidates</span>
                </button>
              </div>
            ) : viewMode === "table" ? (
              /* High-Density Table View */
              <div className="bg-white rounded-xl border border-slate-200 shadow-2xs overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="bg-slate-50 border-b border-slate-200 text-[10px] font-black text-slate-500 uppercase tracking-widest font-mono">
                        <th className="py-3 px-4">Candidate</th>
                        <th className="py-3 px-4">
                          <div className="flex items-center gap-1.5">
                            <span>Opportunity</span>
                            <select
                              value={filterOpportunityId}
                              onChange={(e) => setFilterOpportunityId(e.target.value)}
                              className="text-[9px] font-bold bg-white border border-slate-200 rounded px-1.5 py-0.5 text-slate-700 uppercase"
                            >
                              <option value="all">All</option>
                              {availableOpportunities.map((opp) => (
                                <option key={opp.id} value={opp.id}>{opp.title}</option>
                              ))}
                            </select>
                          </div>
                        </th>
                        <th className="py-3 px-4">School & District</th>
                        <th className="py-3 px-4 relative">
                          <div className="flex items-center gap-1.5">
                            <span>Status</span>
                            <div className="relative">
                              <button
                                type="button"
                                onClick={() => setStatusHeaderDropdownOpen(!statusHeaderDropdownOpen)}
                                className={`p-1 rounded transition-all cursor-pointer flex items-center justify-center ${
                                  statusFilter !== "all" 
                                    ? "bg-[#0094c8] text-white shadow-2xs" 
                                    : "bg-slate-100 text-slate-500 hover:text-slate-800 hover:bg-slate-200"
                                }`}
                                title="Filter by Status"
                              >
                                <Filter className="w-3.5 h-3.5" />
                                {statusFilter !== "all" && (
                                  <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-amber-400 border border-white" />
                                )}
                              </button>

                              {statusHeaderDropdownOpen && (
                                <div className="absolute left-0 mt-2 w-44 bg-white rounded-xl shadow-xl border border-slate-200 py-1.5 z-50 text-xs font-normal">
                                  <div className="px-3 py-1.5 text-[9px] font-black text-slate-400 uppercase tracking-widest font-mono border-b border-slate-100">
                                    Filter Status
                                  </div>
                                  {[
                                    { key: "all", label: "All Statuses" },
                                    { key: "pending", label: "Needs Review" },
                                    { key: "interview", label: "Interviewing" },
                                    { key: "accepted", label: "Accepted" },
                                    { key: "rejected", label: "Declined" },
                                  ].map((opt) => (
                                    <button
                                      key={opt.key}
                                      type="button"
                                      onClick={() => {
                                        setStatusFilter(opt.key as any);
                                        setStatusHeaderDropdownOpen(false);
                                      }}
                                      className={`w-full text-left px-3 py-2 hover:bg-slate-50 flex items-center justify-between font-medium cursor-pointer ${
                                        statusFilter === opt.key ? "text-[#0094c8] font-bold bg-blue-50/50" : "text-slate-700"
                                      }`}
                                    >
                                      <span>{opt.label}</span>
                                      {statusFilter === opt.key && <Check className="w-3.5 h-3.5 text-[#0094c8]" />}
                                    </button>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        </th>
                        <th className="py-3 px-4 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 text-xs">
                      {organizedApps.map((app) => {
                        const studentProfile = mergedUsers.find(
                          (u) =>
                            (u.uid && app.studentId && u.uid === app.studentId) ||
                            (u.email && app.studentEmail && u.email.toLowerCase().trim() === app.studentEmail.toLowerCase().trim())
                        );
                        const opp = opportunities.find((o) => o.id === app.opportunityId);
                        const isExpanded = !!expandedAppIds[app.id];

                        return (
                          <React.Fragment key={app.id}>
                            <tr className="hover:bg-slate-50/80 transition-colors">
                              <td className="py-3 px-4">
                                <div className="flex items-center space-x-3">
                                  <div className="w-11 h-11 rounded-xl overflow-hidden shrink-0 border border-slate-200/90 bg-slate-50 shadow-xs flex items-center justify-center">
                                    <StudentAvatar
                                      studentId={app.studentId}
                                      studentName={app.studentName}
                                      studentEmail={app.studentEmail}
                                      profile={studentProfile}
                                    />
                                  </div>
                                  <div className="min-w-0">
                                    <div className="flex items-center gap-1">
                                      <span className="font-bold text-slate-900 truncate uppercase">
                                        {formatLastNameFirst(app.studentName, studentProfile)}
                                      </span>
                                      {studentProfile?.verified && (
                                        <span className="inline-flex items-center gap-0.5 text-[7px] font-black text-sky-700 bg-sky-50 px-1 py-0.2 rounded border border-sky-100 uppercase">
                                          <Check className="w-2 h-2 text-sky-600" />
                                        </span>
                                      )}
                                    </div>
                                    <span className="text-[10px] text-slate-400 font-mono block truncate">
                                      {app.studentEmail || studentProfile?.email || "No email"}
                                    </span>
                                  </div>
                                </div>
                              </td>
                              <td className="py-3 px-4 font-semibold text-slate-800">
                                <span className="truncate max-w-[200px] block" title={opp?.title || app.opportunityTitle}>
                                  {opp?.title || app.opportunityTitle || "Opportunity"}
                                </span>
                              </td>
                              <td className="py-3 px-4">
                                <div className="min-w-0">
                                  <span className="font-medium text-slate-700 block truncate">
                                    {schools.find((s) => s.id === studentProfile?.school)?.name || studentProfile?.school || "School N/A"}
                                  </span>
                                  <span className="text-[10px] text-slate-400 block truncate uppercase">
                                    {districts.find((d) => d.id === studentProfile?.district)?.name || studentProfile?.district || "District N/A"}
                                  </span>
                                </div>
                              </td>
                              <td className="py-3 px-4 whitespace-nowrap">
                                {app.status === "accepted" ? (
                                  <span className="inline-flex items-center gap-1 text-[10px] uppercase font-bold px-2 py-0.5 rounded-md bg-emerald-50 text-emerald-700 border border-emerald-200 font-mono">
                                    <CheckCircle className="w-3 h-3 text-emerald-600" />
                                    Accepted
                                  </span>
                                ) : app.status === "rejected" ? (
                                  <span className="inline-flex items-center gap-1 text-[10px] uppercase font-bold px-2 py-0.5 rounded-md bg-rose-50 text-rose-700 border border-rose-200 font-mono">
                                    <XCircle className="w-3 h-3 text-rose-600" />
                                    Declined
                                  </span>
                                ) : app.interviewScheduled || app.status === "reviewed" ? (
                                  <span className="inline-flex items-center gap-1 text-[10px] uppercase font-bold px-2 py-0.5 rounded-md bg-blue-50 text-blue-700 border border-blue-200 font-mono">
                                    <Calendar className="w-3 h-3 text-blue-600" />
                                    Interview Scheduled
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center gap-1 text-[10px] uppercase font-bold px-2 py-0.5 rounded-md bg-amber-50 text-amber-700 border border-amber-200 font-mono">
                                    <Clock className="w-3 h-3 text-amber-600" />
                                    Pending Review
                                  </span>
                                )}
                              </td>

                              <td className="py-3 px-4 text-right whitespace-nowrap">
                                <div className="inline-flex items-center gap-1">
                                  <button
                                    type="button"
                                    onClick={() => handleReview(app.id, "accepted")}
                                    className="p-1.5 bg-emerald-50 text-emerald-600 hover:bg-emerald-600 hover:text-white rounded-lg transition-all cursor-pointer"
                                    title="Accept Candidate"
                                  >
                                    <Check className="w-3.5 h-3.5" />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => handleReview(app.id, "rejected")}
                                    className="p-1.5 bg-rose-50 text-rose-600 hover:bg-rose-600 hover:text-white rounded-lg transition-all cursor-pointer"
                                    title="Decline Candidate"
                                  >
                                    <X className="w-3.5 h-3.5" />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const matchingInterview = interviews?.find((i) => i.applicationId === app.id);
                                      if (matchingInterview) {
                                        setInterviewData({
                                          date: matchingInterview.date || "",
                                          time: matchingInterview.time || "",
                                          person: matchingInterview.person || "",
                                          mode: matchingInterview.mode || "virtual",
                                          link: matchingInterview.link || "",
                                          address: matchingInterview.address || "",
                                          phone: matchingInterview.phone || "",
                                        });
                                      } else {
                                        setInterviewData({ date: "", time: "", person: "", mode: "virtual", link: "", address: "", phone: "" });
                                      }
                                      setInterviewModal({ show: true, app });
                                    }}
                                    className="p-1.5 bg-blue-50 text-blue-600 hover:bg-blue-600 hover:text-white rounded-lg transition-all cursor-pointer"
                                    title={app.interviewScheduled ? "Reschedule Interview" : "Schedule Interview"}
                                  >
                                    <Calendar className="w-3.5 h-3.5" />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => toggleAppDetails(app.id)}
                                    className={`p-1.5 rounded-lg transition-all flex items-center justify-center border cursor-pointer ${
                                      isExpanded
                                        ? "bg-slate-800 text-white border-slate-900"
                                        : "bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100"
                                    }`}
                                    title={isExpanded ? "Close Details" : "View Application Details"}
                                  >
                                    {isExpanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                                  </button>
                                </div>
                              </td>
                            </tr>
                            {isExpanded && (
                              <tr>
                                <td colSpan={6} className="p-0 bg-slate-50/50">
                                  <div className="p-4 sm:p-6 border-y border-slate-200/80">
                                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
                                      <div className="lg:col-span-5 space-y-4">
                                        <CandidateProfile
                                          studentId={app.studentId}
                                          studentName={app.studentName}
                                          studentEmail={app.studentEmail}
                                        />
                                      </div>
                                      <div className="lg:col-span-4 space-y-4">
                                        <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-2xs space-y-3">
                                          <div className="flex items-center gap-2">
                                            <Clipboard className="w-4 h-4 text-[#0094c8]" />
                                            <h4 className="text-[10px] font-black text-slate-700 uppercase tracking-wider font-mono">
                                              Supplemental Questions & Disclosures
                                            </h4>
                                          </div>
                                          {renderCoverLetterOrQA(app.coverLetter)}
                                        </div>
                                      </div>
                                      <div className="lg:col-span-3 space-y-4">
                                        <div className="bg-[#0f2d4a] rounded-xl p-4 text-white shadow-sm space-y-3">
                                          <h5 className="text-[10px] font-black text-white/50 uppercase tracking-widest font-mono">
                                            Candidate Status & Decision
                                          </h5>
                                          <div className="space-y-2 text-xs">
                                            <div className="flex items-center justify-between font-mono">
                                              <span className="text-white/60 text-[11px] uppercase">Status:</span>
                                              <span className="font-bold text-[#0094c8] uppercase">{app.status}</span>
                                            </div>
                                            {app.interviewScheduled && (
                                              <div className="p-2.5 bg-white/10 rounded-lg text-xs space-y-1">
                                                <div className="flex items-center gap-1.5 text-emerald-400 font-bold uppercase text-[10px]">
                                                  <Calendar className="w-3 h-3" />
                                                  Interview Scheduled
                                                </div>
                                                {interviews.find((i) => i.applicationId === app.id)?.date && (
                                                  <p className="text-white/80 font-mono text-[11px]">
                                                    {interviews.find((i) => i.applicationId === app.id)?.date} @{" "}
                                                    {interviews.find((i) => i.applicationId === app.id)?.time}
                                                  </p>
                                                )}
                                              </div>
                                            )}
                                          </div>
                                          <div className="pt-2 border-t border-white/10 space-y-2">
                                            <button
                                              type="button"
                                              onClick={() => handleReview(app.id, "accepted")}
                                              className="w-full py-1.5 px-3 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg text-[10px] font-bold uppercase tracking-wider flex items-center justify-center gap-1.5 transition-all shadow-xs cursor-pointer"
                                            >
                                              <Check className="w-3.5 h-3.5" />
                                              Accept Candidate
                                            </button>
                                            <button
                                              type="button"
                                              onClick={() => handleReview(app.id, "rejected")}
                                              className="w-full py-1.5 px-3 bg-rose-900/70 hover:bg-rose-800 text-rose-200 border border-rose-700/50 rounded-lg text-[10px] font-bold uppercase tracking-wider flex items-center justify-center gap-1.5 transition-all cursor-pointer"
                                            >
                                              <X className="w-3.5 h-3.5" />
                                              Decline Candidate
                                            </button>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              /* Modern, Compact Grouped Card View */
              <div className="space-y-6">
                {Object.entries(groupedApps as Record<string, Application[]>).map(
                  ([oppId, apps]) => {
                    const opportunity = opportunities.find((o) => o.id === oppId);
                    const hiredCount = apps.filter((a) => a.status === "accepted").length;
                    const pendingCount = apps.filter((a) => (a.status as string) === "pending" || (a.status as string) === "submitted" || (a.status as string) === "under_review").length;
                    const interviewCount = apps.filter((a) => a.interviewScheduled || (a.status as string) === "reviewed" || (a.status as string) === "interviewing" || (a.status as string) === "scheduled").length;
                    const orgProfile = mergedUsers.find(
                      (u) =>
                        u.orgName ===
                        (opportunity?.organizationName || apps[0]?.organizationName)
                    );
                    const isCollapsed = !!collapsedOpps[oppId];

                    return (
                      <div key={oppId} className="bg-white rounded-2xl border border-slate-200/90 shadow-xs overflow-hidden">
                        {/* Compact Opportunity Header matching image */}
                        <div
                          onClick={() =>
                            setCollapsedOpps((prev) => ({
                              ...prev,
                              [oppId]: !prev[oppId],
                            }))
                          }
                          className="flex flex-col sm:flex-row sm:items-center justify-between p-4 sm:px-6 sm:py-4 cursor-pointer select-none bg-white hover:bg-slate-50/50 border-b border-slate-200/80 transition-colors gap-3"
                        >
                          <div className="flex items-center space-x-4 min-w-0">
                            <div className="w-16 h-16 rounded-xl border border-slate-200 bg-white p-1 flex items-center justify-center shrink-0 shadow-2xs">
                              {(() => {
                                const logoUrl = orgProfile?.avatar || opportunity?.externalLogoUrl;
                                const hasLogo = logoUrl && isUrl(logoUrl);
                                if (!hasLogo) {
                                  return (
                                    <ProfessionalOrgFallback
                                      orgName={
                                        opportunity?.organizationName ||
                                        apps[0]?.organizationName ||
                                        "Company"
                                      }
                                      orgId={opportunity?.organizationId || undefined}
                                      variant="square"
                                      className="w-full h-full text-[9px]"
                                      sector={opportunity?.careerCluster || opportunity?.industry}
                                    />
                                  );
                                }
                                return (
                                  <StudentAvatar
                                    studentId={opportunity?.organizationId || undefined}
                                    studentName={
                                      opportunity?.organizationName ||
                                      apps[0]?.organizationName ||
                                      "Company"
                                    }
                                    profile={orgProfile}
                                    imageUrl={logoUrl}
                                    className="w-full h-full object-cover"
                                  />
                                );
                              })()}
                            </div>
                            <div className="min-w-0">
                              <h3 className="text-sm sm:text-base font-extrabold text-[#0f2d4a] uppercase tracking-tight truncate">
                                {opportunity?.title ||
                                  apps[0]?.opportunityTitle ||
                                  "Unknown Opportunity"}
                              </h3>
                              <div className="flex flex-wrap items-center gap-1.5 text-[11px] text-slate-500 mt-0.5">
                                {(opportunity?.careerCluster || opportunity?.industry) && (
                                  <span className="font-semibold text-slate-600 bg-slate-50 px-2 py-0.5 rounded-md border border-slate-200 text-[11px]">
                                    {opportunity.careerCluster || opportunity.industry}
                                  </span>
                                )}
                                {(opportunity?.term || opportunity?.semester) && (
                                  <span className="text-slate-400 font-medium">
                                    • {opportunity.term || opportunity.semester}
                                  </span>
                                )}
                                {(opportunity?.externalContactEmail || apps[0]?.externalContactEmail) && (
                                  <span className="text-[#0094c8] lowercase flex items-center gap-0.5">
                                    <Mail className="w-2.5 h-2.5" />
                                    {opportunity?.externalContactEmail || apps[0]?.externalContactEmail}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>

                          <div className="flex items-center space-x-2.5 shrink-0 self-end sm:self-center">
                            {/* Candidate count pills */}
                            <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase font-bold">
                              <span className="px-2.5 py-1 rounded-md bg-white text-slate-700 border border-slate-200">
                                {apps.length} Total
                              </span>
                              {pendingCount > 0 && (
                                <span className="px-2.5 py-1 rounded-md bg-amber-50 text-amber-700 border border-amber-200">
                                  {pendingCount} Pending
                                </span>
                              )}
                              {interviewCount > 0 && (
                                <span className="px-2.5 py-1 rounded-md bg-blue-50 text-blue-700 border border-blue-200">
                                  {interviewCount} Interview
                                </span>
                              )}
                              {hiredCount > 0 && (
                                <span className="px-2.5 py-1 rounded-md bg-emerald-50 text-emerald-700 border border-emerald-200">
                                  {hiredCount} Hired
                                </span>
                              )}
                            </div>

                            <div className="w-9 h-9 rounded-lg bg-white border border-slate-200 text-slate-400 hover:text-slate-600 hover:bg-slate-50 flex items-center justify-center transition-colors">
                              {isCollapsed ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
                            </div>
                          </div>
                        </div>

                        {/* Candidates List under Opportunity */}
                        {!isCollapsed && (
                          <div className="divide-y divide-slate-100">
                            {apps.map((app) => {
                              const isExpanded = !!expandedAppIds[app.id];
                              const studentProfile = mergedUsers.find(
                                (u) =>
                                  (u.uid && app.studentId && u.uid === app.studentId) ||
                                  (u.email &&
                                    app.studentEmail &&
                                    u.email.toLowerCase().trim() ===
                                      app.studentEmail.toLowerCase().trim())
                              );

                              const statusBorder =
                                app.status === "accepted"
                                  ? "border-l-emerald-500"
                                  : app.status === "rejected"
                                  ? "border-l-rose-500"
                                  : app.interviewScheduled || app.status === "reviewed"
                                  ? "border-l-blue-500"
                                  : "border-l-amber-400";

                              return (
                                <div
                                  key={app.id}
                                  className={`border-l-4 ${statusBorder} bg-white hover:bg-slate-50/50 transition-colors`}
                                >
                                  <div className="flex flex-col md:flex-row md:items-center justify-between p-4 sm:px-6 sm:py-4 gap-4">
                                    {/* Left: Avatar + Candidate Name + Email */}
                                    <div className="flex items-center space-x-3.5 min-w-0 md:w-[32%] shrink-0">
                                      <div className="w-11 h-11 rounded-full overflow-hidden shrink-0 border border-slate-200 bg-slate-50 shadow-2xs">
                                        <StudentAvatar
                                          studentId={app.studentId}
                                          studentName={app.studentName}
                                          studentEmail={app.studentEmail}
                                          profile={studentProfile}
                                        />
                                      </div>
                                      <div className="min-w-0">
                                        <div className="flex items-center gap-1.5 flex-wrap">
                                          <h4 className="text-sm font-bold text-slate-900 uppercase tracking-tight truncate">
                                            {formatLastNameFirst(app.studentName, studentProfile)}
                                          </h4>
                                          {studentProfile?.verified && (
                                            <span className="inline-flex items-center gap-1 text-[8px] font-black text-sky-700 bg-sky-50 px-1.5 py-0.5 rounded border border-sky-200 uppercase tracking-wider font-mono shrink-0">
                                              <Check className="w-2.5 h-2.5 text-sky-600" />
                                              Verified
                                            </span>
                                          )}
                                        </div>
                                        <p className="text-xs text-slate-400 truncate font-mono mt-0.5">
                                          {app.studentEmail || studentProfile?.email || "No email recorded"}
                                        </p>
                                      </div>
                                    </div>

                                    {/* Middle: School & District */}
                                    <div className="flex flex-col md:w-[28%] text-left shrink-0">
                                      <span className="text-sm font-semibold text-slate-800 truncate">
                                        {schools.find((s) => s.id === studentProfile?.school)?.name ||
                                          studentProfile?.school ||
                                          "School Not Listed"}
                                      </span>
                                      <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider font-mono truncate mt-0.5">
                                        {districts.find((d) => d.id === studentProfile?.district)?.name ||
                                          studentProfile?.district ||
                                          "Regional School District"}
                                        {studentProfile?.classYear ? ` • Class of ${studentProfile.classYear}` : ""}
                                      </span>
                                    </div>

                                    {/* Status Badge - Outlined pill matching screenshot */}
                                    <div className="flex items-center gap-1.5 shrink-0">
                                      {app.status === "accepted" ? (
                                        <span className="px-3 py-1 rounded-lg border border-emerald-300 bg-emerald-50/70 text-emerald-700 text-xs font-bold tracking-wider flex items-center gap-1.5 font-mono">
                                          <CheckCircle className="w-3.5 h-3.5 text-emerald-600" />
                                          ACCEPTED
                                        </span>
                                      ) : app.status === "rejected" ? (
                                        <span className="px-3 py-1 rounded-lg border border-rose-300 bg-rose-50/70 text-rose-700 text-xs font-bold tracking-wider flex items-center gap-1.5 font-mono">
                                          <XCircle className="w-3.5 h-3.5 text-rose-600" />
                                          DECLINED
                                        </span>
                                      ) : app.interviewScheduled || app.status === "reviewed" ? (
                                        <span className="px-3 py-1 rounded-lg border border-blue-300 bg-blue-50/70 text-blue-700 text-xs font-bold tracking-wider flex items-center gap-1.5 font-mono">
                                          <Calendar className="w-3.5 h-3.5 text-blue-600" />
                                          INTERVIEW
                                        </span>
                                      ) : (
                                        <span className="px-3 py-1 rounded-lg border border-amber-300 bg-amber-50/70 text-amber-700 text-xs font-bold tracking-wider flex items-center gap-1.5 font-mono">
                                          <Clock className="w-3.5 h-3.5 text-amber-600" />
                                          PENDING
                                        </span>
                                      )}
                                    </div>

                                    {/* Right: Actions Toolbar (5 buttons exactly as in screenshot) */}
                                    <div className="flex items-center gap-1.5 shrink-0 ml-auto md:ml-0">


                                      {/* Accept */}
                                      <button
                                        type="button"
                                        onClick={() => handleReview(app.id, "accepted")}
                                        className="w-9 h-9 rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-600 hover:bg-emerald-100 hover:text-emerald-700 flex items-center justify-center transition-colors cursor-pointer shadow-2xs"
                                        title="Accept Candidate"
                                      >
                                        <Check className="w-4 h-4 stroke-[2.5]" />
                                      </button>

                                      {/* Decline */}
                                      <button
                                        type="button"
                                        onClick={() => handleReview(app.id, "rejected")}
                                        className="w-9 h-9 rounded-lg border border-rose-200 bg-rose-50 text-rose-600 hover:bg-rose-100 hover:text-rose-700 flex items-center justify-center transition-colors cursor-pointer shadow-2xs"
                                        title="Decline Candidate"
                                      >
                                        <X className="w-4 h-4 stroke-[2.5]" />
                                      </button>

                                      {/* Schedule / Reschedule Interview */}
                                      <button
                                        type="button"
                                        onClick={() => {
                                          const matchingInterview = interviews?.find((i) => i.applicationId === app.id);
                                          if (matchingInterview) {
                                            setInterviewData({
                                              date: matchingInterview.date || "",
                                              time: matchingInterview.time || "",
                                              person: matchingInterview.person || "",
                                              mode: matchingInterview.mode || "virtual",
                                              link: matchingInterview.link || "",
                                              address: matchingInterview.address || "",
                                              phone: matchingInterview.phone || "",
                                            });
                                          } else {
                                            setInterviewData({ date: "", time: "", person: "", mode: "virtual", link: "", address: "", phone: "" });
                                          }
                                          setInterviewModal({ show: true, app });
                                        }}
                                        className="w-9 h-9 rounded-lg border border-blue-200 bg-blue-50 text-blue-600 hover:bg-blue-100 hover:text-blue-700 flex items-center justify-center transition-colors cursor-pointer shadow-2xs"
                                        title={app.interviewScheduled ? "Reschedule Interview" : "Schedule Interview"}
                                      >
                                        <Calendar className="w-4 h-4" />
                                      </button>

                                      {/* Details toggle */}
                                      <button
                                        type="button"
                                        onClick={() => toggleAppDetails(app.id)}
                                        className="w-9 h-9 rounded-lg border border-slate-200 bg-white text-slate-500 hover:bg-slate-50 hover:text-slate-800 flex items-center justify-center transition-colors cursor-pointer shadow-2xs"
                                        title={isExpanded ? "Close Application Details" : "View Application Details"}
                                      >
                                        {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                                      </button>
                                    </div>
                                  </div>

                                  {/* Expandable Candidate Details */}
                                  {isExpanded && (
                                    <div className="border-t border-slate-100 bg-slate-50/50 p-4 sm:p-6 animate-in slide-in-from-top-2 duration-200">
                                      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
                                        {/* Column 1: Profile & Resume */}
                                        <div className="lg:col-span-5 space-y-4">
                                          <CandidateProfile
                                            studentId={app.studentId}
                                            studentName={app.studentName}
                                            studentEmail={app.studentEmail}
                                          />
                                        </div>

                                        {/* Column 2: Supplemental QA / Cover letter */}
                                        <div className="lg:col-span-4 space-y-4">
                                          <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-2xs space-y-3">
                                            <div className="flex items-center gap-2">
                                              <Clipboard className="w-4 h-4 text-[#0094c8]" />
                                              <h4 className="text-[10px] font-black text-slate-700 uppercase tracking-wider font-mono">
                                                Supplemental Questions & Disclosures
                                              </h4>
                                            </div>
                                            {renderCoverLetterOrQA(app.coverLetter)}
                                          </div>
                                        </div>

                                        {/* Column 3: Decision & Engagement */}
                                        <div className="lg:col-span-3 space-y-4">
                                          <div className="bg-[#0f2d4a] rounded-xl p-4 text-white shadow-sm space-y-4">
                                            <h5 className="text-[10px] font-black text-white/50 uppercase tracking-widest font-mono">
                                              Candidate Status & Actions
                                            </h5>
                                            <div className="space-y-3 text-xs">
                                              <div className="flex items-center justify-between font-mono">
                                                <span className="text-white/60 text-[11px] uppercase">Current Status:</span>
                                                <span className="font-bold text-[#0094c8] uppercase">{app.status}</span>
                                              </div>
                                              {app.interviewScheduled && (
                                                <div className="p-2.5 bg-white/10 rounded-lg text-xs space-y-1">
                                                  <div className="flex items-center gap-1.5 text-emerald-400 font-bold uppercase text-[10px]">
                                                    <Calendar className="w-3 h-3" />
                                                    Interview Scheduled
                                                  </div>
                                                  {interviews.find((i) => i.applicationId === app.id)?.date && (
                                                    <p className="text-white/80 font-mono text-[11px]">
                                                      {interviews.find((i) => i.applicationId === app.id)?.date} @{" "}
                                                      {interviews.find((i) => i.applicationId === app.id)?.time}
                                                    </p>
                                                  )}
                                                </div>
                                              )}
                                            </div>

                                            <div className="pt-2 border-t border-white/10 space-y-2">
                                              <button
                                                type="button"
                                                onClick={() => handleReview(app.id, "accepted")}
                                                className="w-full py-2 px-3 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg text-[10px] font-bold uppercase tracking-wider flex items-center justify-center gap-1.5 transition-all shadow-xs cursor-pointer"
                                              >
                                                <Check className="w-3.5 h-3.5" />
                                                Accept Candidate
                                              </button>
                                              <button
                                                type="button"
                                                onClick={() => {
                                                  const matchingInterview = interviews?.find((i) => i.applicationId === app.id);
                                                  if (matchingInterview) {
                                                    setInterviewData({
                                                      date: matchingInterview.date || "",
                                                      time: matchingInterview.time || "",
                                                      person: matchingInterview.person || "",
                                                      mode: matchingInterview.mode || "virtual",
                                                      link: matchingInterview.link || "",
                                                      address: matchingInterview.address || "",
                                                      phone: matchingInterview.phone || "",
                                                    });
                                                  } else {
                                                    setInterviewData({ date: "", time: "", person: "", mode: "virtual", link: "", address: "", phone: "" });
                                                  }
                                                  setInterviewModal({ show: true, app });
                                                }}
                                                className="w-full py-2 px-3 bg-[#0094c8] hover:bg-[#007ba6] text-white rounded-lg text-[10px] font-bold uppercase tracking-wider flex items-center justify-center gap-1.5 transition-all shadow-xs cursor-pointer"
                                              >
                                                <Calendar className="w-3.5 h-3.5" />
                                                {app.interviewScheduled ? "Reschedule Interview" : "Schedule Interview"}
                                              </button>
                                              <button
                                                type="button"
                                                onClick={() => handleReview(app.id, "rejected")}
                                                className="w-full py-2 px-3 bg-rose-900/60 hover:bg-rose-800 text-rose-200 border border-rose-700/50 rounded-lg text-[10px] font-bold uppercase tracking-wider flex items-center justify-center gap-1.5 transition-all cursor-pointer"
                                              >
                                                <X className="w-3.5 h-3.5" />
                                                Decline Candidate
                                              </button>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  }
                )}
              </div>
            )
          ) : organizedApps.length === 0 ? (
            <div className="bg-white border border-slate-200 rounded-2xl p-10 text-center shadow-2xs space-y-3 max-w-lg mx-auto">
              <div className="mx-auto w-10 h-10 bg-slate-100 text-slate-400 rounded-full flex items-center justify-center">
                <Filter className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-base font-bold text-slate-800">No matching applications</h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  No applications match your current search and status filter criteria.
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setStatusFilter("all");
                  setFilterOpportunityId("all");
                  setSearchQuery("");
                }}
                className="px-4 py-1.5 bg-[#0094c8] text-white text-xs font-bold rounded-lg uppercase tracking-wider hover:bg-[#007ba6] cursor-pointer inline-flex items-center gap-1.5"
              >
                <RotateCcw className="w-3.5 h-3.5" />
                <span>View All Applications</span>
              </button>
            </div>
          ) : (
            <div className="bg-white rounded-2xl border border-slate-200/90 shadow-xs overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200 text-[10px] font-black uppercase tracking-widest text-slate-500 font-mono select-none">
                      <th className="py-3.5 px-4 w-64">
                        <button
                          type="button"
                          onClick={() => setSortBy(sortBy === "name_asc" ? "name_desc" : "name_asc")}
                          className="group inline-flex items-center gap-1.5 hover:text-slate-700 transition-colors uppercase cursor-pointer"
                          title={userRole === "student" ? "Sort by Opportunity" : "Sort by Candidate Name"}
                        >
                          <span>{userRole === "student" ? "Opportunity & Host" : "Applicant Candidate"}</span>
                          <ArrowUpDown className={`w-3 h-3 ${sortBy === "name_asc" || sortBy === "name_desc" ? "text-[#0094c8]" : "text-slate-300 group-hover:text-slate-500"}`} />
                        </button>
                      </th>
                      <th className="py-3.5 px-4 w-60">
                        <span>{userRole === "student" ? "Pathway & Term" : "Opportunity & School"}</span>
                      </th>
                      <th className="py-3.5 px-4 text-center min-w-[320px]">Progress Timeline</th>
                      <th className="py-3.5 px-4 text-center w-36 relative">
                        <div className="flex items-center justify-center gap-1.5">
                          <button
                            type="button"
                            onClick={() => setSortBy(sortBy === "status" ? "status_desc" : "status")}
                            className="group inline-flex items-center gap-1 hover:text-slate-700 transition-colors uppercase cursor-pointer"
                            title="Sort by Status"
                          >
                            <span>Status</span>
                            <ArrowUpDown className={`w-3 h-3 ${sortBy === "status" || sortBy === "status_desc" ? "text-[#0094c8]" : "text-slate-300 group-hover:text-slate-500"}`} />
                          </button>
                          <div className="relative" ref={statusDropdownRef}>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setStatusHeaderDropdownOpen(!statusHeaderDropdownOpen);
                              }}
                              className={`p-1 rounded transition-all cursor-pointer flex items-center justify-center ${
                                statusFilter !== "all"
                                  ? "bg-[#0094c8] text-white shadow-2xs"
                                  : "bg-slate-100 text-slate-500 hover:text-slate-800 hover:bg-slate-200"
                              }`}
                              title="Filter by Status"
                            >
                              <Filter className="w-3.5 h-3.5" />
                              {statusFilter !== "all" && (
                                <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-amber-400 border border-white" />
                              )}
                            </button>

                            {statusHeaderDropdownOpen && (
                              <div
                                onClick={(e) => e.stopPropagation()}
                                className="absolute left-1/2 -translate-x-1/2 sm:left-auto sm:right-0 sm:translate-x-0 mt-2 w-52 bg-white rounded-xl shadow-xl border border-slate-200 py-1.5 z-50 text-xs font-normal normal-case text-slate-800"
                              >
                                <div className="px-3 py-1.5 text-[9px] font-black text-slate-400 uppercase tracking-widest font-mono border-b border-slate-100 flex items-center justify-between">
                                  <span>Filter Status</span>
                                  {statusFilter !== "all" && (
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setStatusFilter("all");
                                        setStatusHeaderDropdownOpen(false);
                                      }}
                                      className="text-[#0094c8] hover:underline cursor-pointer lowercase font-sans text-[10px]"
                                    >
                                      clear
                                    </button>
                                  )}
                                </div>
                                {[
                                  { key: "all", label: "All Applications", count: reviewStats.total },
                                  { key: "pending", label: "Needs Review", count: reviewStats.pending },
                                  { key: "interview", label: "Interviewing", count: reviewStats.interview },
                                  { key: "accepted", label: "Accepted", count: reviewStats.accepted },
                                  { key: "rejected", label: "Declined", count: reviewStats.rejected },
                                ].map((opt) => (
                                  <button
                                    key={opt.key}
                                    type="button"
                                    onClick={() => {
                                      setStatusFilter(opt.key as any);
                                      setStatusHeaderDropdownOpen(false);
                                    }}
                                    className={`w-full px-3 py-2 text-left flex items-center justify-between hover:bg-slate-50 transition-colors cursor-pointer ${
                                      statusFilter === opt.key ? "text-[#0094c8] font-bold bg-[#0094c8]/5" : "text-slate-600"
                                    }`}
                                  >
                                    <div className="flex items-center gap-2">
                                      <span className={`w-1.5 h-1.5 rounded-full ${
                                        opt.key === "accepted"
                                          ? "bg-emerald-500"
                                          : opt.key === "rejected"
                                          ? "bg-rose-500"
                                          : opt.key === "interview"
                                          ? "bg-sky-500"
                                          : opt.key === "pending"
                                          ? "bg-amber-500"
                                          : "bg-slate-400"
                                      }`} />
                                      <span>{opt.label}</span>
                                    </div>
                                    <span className="text-[10px] font-mono px-1.5 py-0.2 rounded-full bg-slate-100 text-slate-500">
                                      {opt.count}
                                    </span>
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </th>
                      <th className="py-3.5 px-6 text-right w-44">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                {organizedApps.map((app) => {
                  const matchingOpportunity =
                    opportunities.find((o) => o.id === app.opportunityId) || null;
                  const studentProfile = mergedUsers.find(
                    (u) =>
                      (u.uid && app.studentId && u.uid === app.studentId) ||
                      (u.email &&
                        app.studentEmail &&
                        u.email.toLowerCase().trim() ===
                          app.studentEmail.toLowerCase().trim()))
                  const isExpanded = !!expandedAppIds[app.id];

                  const orgProfile = mergedUsers?.find((u) => {
                    if (
                      matchingOpportunity?.organizationId &&
                      u.uid === matchingOpportunity.organizationId
                    )
                      return true;
                    const companyName = (
                      app.organizationName ||
                      matchingOpportunity?.organizationName ||
                      ""
                    )
                      .toLowerCase()
                      .trim()
                    const uOrgName = (u.orgName || u.name || "")
                      .toLowerCase()
                      .trim()
                    return uOrgName === companyName;
                  })

                  const statusBorder =
                    app.status === "accepted"
                      ? "border-l-4 border-l-emerald-500"
                      : app.status === "rejected"
                      ? "border-l-4 border-l-rose-500"
                      : app.interviewScheduled || app.status === "reviewed" || app.status === "interviewing"
                      ? "border-l-4 border-l-sky-500"
                      : "border-l-4 border-l-slate-300";

                  return (
                    <React.Fragment key={app.id}>
                      <tr
                        className={`group border-l-4 ${statusBorder} bg-white hover:bg-slate-50/40 transition-colors`}
                      >
                        {userRole === "student" ? (
                          <>
                            {/* Col 1: Opportunity & Host */}
                            <td className="py-3.5 px-4 align-middle">
                              <div className="flex items-center space-x-3.5 min-w-0 xl:w-64 shrink-0">
                                <div className="w-11 h-11 rounded-xl border border-slate-200/90 bg-slate-50 p-1 shrink-0 flex items-center justify-center overflow-hidden shadow-2xs">
                                  {(() => {
                                    const resolvedLogo = orgProfile?.avatar || matchingOpportunity?.externalLogoUrl;
                                    const hasLogo = resolvedLogo && isUrl(resolvedLogo);
                                    if (!hasLogo) {
                                      return (
                                        <ProfessionalOrgFallback
                                          orgName={app.organizationName || matchingOpportunity?.organizationName || "Company"}
                                          orgId={matchingOpportunity?.organizationId || undefined}
                                          variant="square"
                                          className="w-full h-full text-[10px]"
                                          sector={matchingOpportunity?.careerCluster || matchingOpportunity?.industry || orgProfile?.industry}
                                        />
                                      );
                                    }
                                    return (
                                      <StudentAvatar
                                        studentId={matchingOpportunity?.organizationId || undefined}
                                        studentName={app.organizationName || matchingOpportunity?.organizationName || "Company"}
                                        profile={orgProfile}
                                        imageUrl={resolvedLogo}
                                        className="w-full h-full object-contain"
                                      />
                                    );
                                  })()}
                                </div>
                                <div className="min-w-0 space-y-0.5">
                                  <h4 className="text-xs sm:text-sm font-bold text-slate-900 group-hover:text-[#0094c8] transition-colors uppercase tracking-tight truncate">
                                    {app.opportunityTitle || matchingOpportunity?.title || "Opportunity"}
                                  </h4>
                                  <div className="flex items-center gap-1 text-[10px] sm:text-[11px] font-semibold text-slate-400 font-mono uppercase truncate">
                                    <span>{app.organizationName || matchingOpportunity?.organizationName || "Host Organization"}</span>
                                    {orgProfile?.verified && (
                                      <Check className="w-3 h-3 text-sky-600 shrink-0 stroke-[3]" />
                                    )}
                                  </div>
                                </div>
                              </div>
                            </td>

                            {/* Col 2: Pathway & Term */}
                            <td className="py-3.5 px-4 align-middle">
                              <div className="flex flex-col xl:w-60 text-left shrink-0 min-w-0">
                                <span className="text-xs sm:text-sm font-bold text-slate-800 uppercase tracking-tight truncate">
                                  {matchingOpportunity?.careerCluster || matchingOpportunity?.industry || "Work-Based Learning"}
                                </span>
                                <span className="text-[10px] sm:text-[11px] font-semibold text-slate-400 font-mono uppercase truncate mt-0.5">
                                  Term: {app.term || matchingOpportunity?.term || matchingOpportunity?.semester || selectedTerm}
                                </span>
                              </div>
                            </td>
                          </>
                        ) : (
                          <>
                            {/* Left: Candidate (fixed w-64 on desktop) */}
                            <td className="py-3.5 px-6 align-middle">
                              <div className="flex items-center space-x-3.5 min-w-0 xl:w-64 shrink-0">
                                <div className="w-11 h-11 rounded-xl border border-slate-200/90 bg-white p-1 shrink-0 flex items-center justify-center overflow-hidden shadow-2xs">
                                  <StudentAvatar
                                    studentId={app.studentId}
                                    studentName={app.studentName}
                                    studentEmail={app.studentEmail}
                                    profile={studentProfile}
                                    className="w-full h-full object-cover rounded-lg"
                                  />
                                </div>

                                <div className="min-w-0">
                                  <div className="flex items-center gap-1.5 flex-wrap">
                                    <h4 className="text-sm font-bold text-slate-900 uppercase tracking-tight truncate">
                                      {formatLastNameFirst(app.studentName, studentProfile)}
                                    </h4>
                                    {studentProfile?.verified && (
                                      <span className="inline-flex items-center text-emerald-600" title="Verified">
                                        <Check className="w-3.5 h-3.5 text-emerald-600 stroke-[3]" />
                                      </span>
                                    )}
                                  </div>
                                  <p className="text-xs text-slate-400 truncate font-mono mt-0.5">
                                    {app.studentEmail || studentProfile?.email || "No email recorded"}
                                  </p>
                                </div>
                              </div>
                            </td>

                            {/* Column 2: Opportunity & Host / School (fixed w-60 on desktop) */}
                            <td className="py-3.5 px-4 align-middle">
                              <div className="flex flex-col xl:w-60 text-left shrink-0 min-w-0">
                                <span className="text-xs sm:text-sm font-bold text-[#0094c8] uppercase tracking-wide truncate">
                                  {app.opportunityTitle || matchingOpportunity?.title || "Opportunity"}
                                </span>
                                <div className="flex items-center gap-1.5 text-[11px] text-slate-500 font-medium mt-0.5 truncate">
                                  <span className="font-semibold text-slate-700 uppercase truncate">
                                    {app.organizationName || matchingOpportunity?.organizationName || "Host Organization"}
                                  </span>
                                  <span className="text-slate-300">•</span>
                                  <span className="text-slate-400 uppercase truncate">
                                    {schools.find((s) => s.id === studentProfile?.school)?.name ||
                                      studentProfile?.school ||
                                      "Regional School"}
                                  </span>
                                </div>
                              </div>
                            </td>
                          </>
                        )}

                      {/* Center: THE PROGRESS BAR (Full 8 steps retained, flexible & aligned!) */}
                      <td className="py-3.5 px-4 align-middle">
                        <div className="flex-1 w-full min-w-[320px] max-w-2xl mx-auto px-1 py-0.5">
                          <div className="bg-slate-50/70 border border-slate-100 rounded-xl px-3 py-2 w-full">
                          {(() => {
                            const matchingPlacement = placements.find(
                              (p) =>
                                p.opportunityId === app.opportunityId &&
                                (p.studentId === app.studentId ||
                                  (p.studentEmail &&
                                    app.studentEmail &&
                                    p.studentEmail.toLowerCase().trim() ===
                                      app.studentEmail
                                        .toLowerCase()
                                        .trim()))
                            );

                            const getStepState = (
                              step: string,
                              idx: number,
                            ) => {
                              if (step === "REC") {
                                const hasRec = recommendations.some(
                                  (r) =>
                                    r.studentId === app.studentId &&
                                    r.opportunityId === app.opportunityId,
                                );
                                return {
                                  isCompleted: hasRec,
                                  isActive: hasRec,
                                };
                              }
                              if (step === "APPLIED") {
                                return {
                                  isCompleted: true,
                                  isActive: app.status === "pending",
                                };
                              }
                              if (step === "INTERVIEW") {
                                const isComp =
                                  ["interviewing", "reviewed", "accepted", "placed", "hired", "active", "scheduled"].includes(app.status) ||
                                  !!app.interviewScheduled ||
                                  (interviews && interviews.some((i) => i.applicationId === app.id || i.studentId === app.studentId || (i.studentEmail && i.studentEmail === app.studentEmail)));
                                return {
                                  isCompleted: isComp,
                                  isActive:
                                    ["interviewing", "reviewed", "scheduled"].includes(app.status) ||
                                    !!app.interviewScheduled,
                                };
                              }
                              if (step === "DECISION") {
                                const isComp = app.status === "accepted" || app.status === "rejected";
                                return {
                                  isCompleted: isComp,
                                  isActive: false,
                                };
                              }
                              if (step === "BOARD") {
                                const isComp = !!matchingPlacement;
                                const isActive =
                                  app.status === "accepted" &&
                                  !matchingPlacement;
                                return { isCompleted: isComp, isActive };
                              }
                              if (step === "EVAL 1") {
                                const isComp =
                                  matchingPlacement?.status === "hired" ||
                                  matchingPlacement?.eval1?.status ===
                                    "released";
                                const isActive =
                                  !!matchingPlacement && !isComp;
                                return { isCompleted: isComp, isActive };
                              }
                              if (step === "EVAL 2") {
                                const isComp =
                                  matchingPlacement?.status === "hired" ||
                                  matchingPlacement?.eval2?.status ===
                                    "released";
                                const isActive =
                                  !!matchingPlacement &&
                                  matchingPlacement?.eval1?.status ===
                                    "released" &&
                                  !isComp;
                                return { isCompleted: isComp, isActive };
                              }
                              if (step === "HIRED") {
                                const isComp =
                                  matchingPlacement?.status === "hired";
                                const isActive =
                                  !!matchingPlacement && !isComp;
                                return { isCompleted: isComp, isActive };
                              }
                              return {
                                isCompleted: false,
                                isActive: false,
                              };
                            };

                            return (
                              <div className="flex items-center justify-between gap-1 md:gap-2 py-0.5">
                                {[
                                  "REC",
                                  "APPLIED",
                                  "INTERVIEW",
                                  "DECISION",
                                  "BOARD",
                                  "EVAL 1",
                                  "EVAL 2",
                                  "HIRED",
                                ].map((step, idx) => {
                                  const { isCompleted, isActive } =
                                    getStepState(step, idx);

                                  if (step === "REC") {
                                    return (
                                      <div
                                        key={step}
                                        className="flex flex-col items-center shrink-0"
                                      >
                                        <div
                                          className={`w-7 h-7 sm:w-8 sm:h-8 rounded-lg flex items-center justify-center shadow-xs border transition-all ${
                                            isCompleted
                                              ? "bg-white border-2 border-[#0094c8] text-[#0094c8] shadow-sm ring-2 ring-[#0094c8]/10"
                                              : "bg-slate-100 border-slate-200/60 text-slate-400"
                                          }`}
                                        >
                                          <User
                                            className={`w-3.5 h-3.5 ${isCompleted ? "text-[#0094c8]" : "text-slate-400"}`}
                                          />
                                        </div>
                                        <span
                                          className={`text-[7px] md:text-[8px] font-black mt-1.5 uppercase tracking-tight block text-center ${
                                            isCompleted
                                              ? "text-[#0094c8]"
                                              : "text-slate-400"
                                          }`}
                                        >
                                          REC
                                        </span>
                                      </div>
                                    );
                                  }

                                  if (step === "BOARD") {
                                    return (
                                      <div
                                        key={step}
                                        className="flex flex-col items-center shrink-0"
                                      >
                                        <div
                                          className={`w-7 h-7 sm:w-8 sm:h-8 rounded-lg flex items-center justify-center transition-all relative ${
                                            isCompleted
                                              ? "bg-white border-2 border-[#0094c8] text-[#0094c8] shadow-sm ring-2 ring-[#0094c8]/10 cursor-pointer hover:bg-slate-50"
                                              : "bg-slate-50 border border-slate-200 text-slate-400 shadow-2xs"
                                          }`}
                                          onClick={() => {
                                            if (
                                              app.status === "accepted" &&
                                              orgProfile?.onboardingText
                                            ) {
                                              setOnboardingModal({
                                                show: true,
                                                content:
                                                  orgProfile.onboardingText,
                                              });
                                            }
                                          }}
                                        >
                                          <ClipboardCheck className="w-3.5 h-3.5" />
                                          {isCompleted && (
                                            <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-[#0094c8] rounded-full border border-white shadow-2xs" />
                                          )}
                                        </div>
                                        <span
                                          className={`text-[7px] md:text-[8px] font-black mt-1.5 uppercase tracking-tight text-center block ${
                                            isCompleted
                                              ? "text-[#0094c8]"
                                              : "text-slate-400"
                                          }`}
                                        >
                                          BOARD
                                        </span>
                                      </div>
                                    );
                                  }

                                  if (step === "HIRED") {
                                    return (
                                      <div
                                        key={step}
                                        className="flex flex-col items-center shrink-0"
                                      >
                                        <div
                                          className={`w-7 h-7 sm:w-8 sm:h-8 rounded-lg flex items-center justify-center transition-all ${
                                            isCompleted
                                              ? "bg-emerald-600 border border-emerald-500 text-white shadow-sm"
                                              : "bg-slate-100 border border-slate-200/60 text-slate-400 shadow-2xs"
                                          }`}
                                        >
                                          <div
                                            className={`w-3.5 h-3.5 rounded-full border flex items-center justify-center ${
                                              isCompleted
                                                ? "border-white text-white"
                                                : "border-slate-350 text-slate-400"
                                            }`}
                                          >
                                            <Check className="w-2 h-2 stroke-[3]" />
                                          </div>
                                        </div>
                                        <span
                                          className={`text-[7px] md:text-[8px] font-black mt-1.5 uppercase tracking-tight text-center block ${
                                            isCompleted
                                              ? "text-emerald-700 font-extrabold"
                                              : "text-slate-400"
                                          }`}
                                        >
                                          HIRED
                                        </span>
                                      </div>
                                    );
                                  }

                                  let barColorClass = "bg-[#0094c8] shadow-2xs shadow-[#0094c8]/10";
                                  let textColorClass = "text-[#0094c8]";
                                  
                                  if (step === "DECISION" && isCompleted) {
                                    if (app.status === "accepted") {
                                      barColorClass = "bg-emerald-500 shadow-2xs shadow-emerald-500/10";
                                      textColorClass = "text-emerald-600";
                                    } else if (app.status === "rejected") {
                                      barColorClass = "bg-rose-500 shadow-2xs shadow-rose-500/10";
                                      textColorClass = "text-rose-500";
                                    }
                                  }

                                  return (
                                    <div
                                      key={step}
                                      className="flex-1 flex flex-col items-center min-w-[18px] md:min-w-[32px]"
                                    >
                                      <div
                                        className={`w-full h-1.5 rounded-full transition-all duration-300 ${
                                          isCompleted
                                            ? barColorClass
                                            : "bg-slate-200/70 border border-slate-200/30"
                                        }`}
                                      />
                                      <span
                                        className={`text-[7px] md:text-[8px] font-black mt-2.5 uppercase tracking-tight text-center block ${
                                          isCompleted
                                            ? textColorClass
                                            : "text-slate-400"
                                        }`}
                                      >
                                        {step}
                                      </span>
                                    </div>
                                  );
                                })}
                              </div>
                            );
                          })()}
                        </div>
                      </div>
                    </td>

                      {/* Column 4: Outlined Status Pill (aligned w-32 across every row) */}
                      <td className="py-3.5 px-4 align-middle text-center whitespace-nowrap">
                        <div className="inline-flex items-center justify-center">
                          {app.status === "accepted" ? (
                            <span className="px-2.5 py-1 rounded-lg border border-emerald-300 bg-emerald-50 text-emerald-700 text-xs font-bold tracking-wider flex items-center gap-1.5 font-mono">
                              <CheckCircle className="w-3.5 h-3.5 text-emerald-600" />
                              ACCEPTED
                            </span>
                          ) : app.status === "rejected" ? (
                            <span className="px-2.5 py-1 rounded-lg border border-rose-300 bg-rose-50 text-rose-700 text-xs font-bold tracking-wider flex items-center gap-1.5 font-mono">
                              <XCircle className="w-3.5 h-3.5 text-rose-600" />
                              DECLINED
                            </span>
                          ) : app.interviewScheduled || app.status === "reviewed" || app.status === "interviewing" ? (
                            <span className="px-2.5 py-1 rounded-lg border border-blue-300 bg-blue-50 text-blue-700 text-xs font-bold tracking-wider flex items-center gap-1.5 font-mono">
                              <Calendar className="w-3.5 h-3.5 text-blue-600" />
                              INTERVIEW
                            </span>
                          ) : (
                            <span className="px-2.5 py-1 rounded-lg border border-amber-300 bg-amber-50 text-amber-700 text-xs font-bold tracking-wider flex items-center gap-1.5 font-mono">
                              <Clock className="w-3.5 h-3.5 text-amber-600" />
                              PENDING
                            </span>
                          )}
                        </div>
                      </td>

                      {/* Column 5: Action Buttons Toolbar (aligned w-44 on right edge) */}
                      <td className="py-3.5 px-6 align-middle text-right whitespace-nowrap">
                        <div className="inline-flex items-center justify-end gap-1.5">
                          {/* Supplemental / details button */}
                          <button
                            type="button"
                            onClick={() => toggleAppDetails(app.id)}
                            className="w-9 h-9 rounded-lg border border-sky-200 bg-sky-50 text-sky-600 hover:bg-sky-100 flex items-center justify-center transition-colors shadow-2xs cursor-pointer active:scale-95"
                            title={isExpanded ? "Close Application Details" : "View Application Details"}
                          >
                            <FileText className="w-4 h-4" />
                          </button>

                          {/* Review actions if reviewer */}
                          {canReview && (
                            <>
                              <button
                                type="button"
                                onClick={() => handleReview(app.id, "accepted")}
                                className="w-9 h-9 rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-600 hover:bg-emerald-100 hover:text-emerald-700 flex items-center justify-center transition-colors cursor-pointer shadow-2xs active:scale-95"
                                title="Accept Application"
                              >
                                <Check className="w-4 h-4 stroke-[2.5]" />
                              </button>
                              <button
                                type="button"
                                onClick={() => handleReview(app.id, "rejected")}
                                className="w-9 h-9 rounded-lg border border-rose-200 bg-rose-50 text-rose-600 hover:bg-rose-100 hover:text-rose-700 flex items-center justify-center transition-colors cursor-pointer shadow-2xs active:scale-95"
                                title="Decline Application"
                              >
                                <X className="w-4 h-4 stroke-[2.5]" />
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  const matchingInterview = interviews?.find((i) => i.applicationId === app.id);
                                  if (matchingInterview) {
                                    setInterviewData({
                                      date: matchingInterview.date || "",
                                      time: matchingInterview.time || "",
                                      person: matchingInterview.person || "",
                                      mode: matchingInterview.mode || "virtual",
                                      link: matchingInterview.link || "",
                                      address: matchingInterview.address || "",
                                      phone: matchingInterview.phone || "",
                                    });
                                  } else {
                                    setInterviewData({ date: "", time: "", person: "", mode: "virtual", link: "", address: "", phone: "" });
                                  }
                                  setInterviewModal({ show: true, app });
                                }}
                                className="w-9 h-9 rounded-lg border border-blue-200 bg-blue-50 text-blue-600 hover:bg-blue-100 hover:text-blue-700 flex items-center justify-center transition-colors cursor-pointer shadow-2xs active:scale-95"
                                title={app.interviewScheduled ? "Reschedule Interview" : "Schedule Interview"}
                              >
                                <Calendar className="w-4 h-4" />
                              </button>
                            </>
                          )}

                          {/* Details toggle chevron */}
                          <button
                            type="button"
                            onClick={() => toggleAppDetails(app.id)}
                            className="w-9 h-9 rounded-lg border border-slate-200 bg-white text-slate-500 hover:bg-slate-100 hover:text-slate-800 flex items-center justify-center transition-colors cursor-pointer shadow-2xs active:scale-95"
                            title={isExpanded ? "Close Application Details" : "View Application Details"}
                          >
                            {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                          </button>
                        </div>
                      </td>
                    </tr>

                    {/* Expandable Application Detail */}
                    {isExpanded && (
                      <tr className="bg-slate-50/20 border-t border-slate-100">
                        <td colSpan={userRole !== "student" ? 5 : 4} className="p-0">
                          <div className="p-8 animate-in slide-in-from-top-2 duration-300">
                        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                          <div className="lg:col-span-7 space-y-6">
                            <div className="bg-white border border-slate-200 rounded-3xl p-7 shadow-sm">
                              <div className="flex items-center gap-3 mb-5">
                                <div className={`w-10 h-10 rounded-2xl flex items-center justify-center ${
                                  canReview 
                                    ? "bg-emerald-50/40 text-emerald-600/80 border border-emerald-500/10" 
                                    : "bg-[#0094c8]/5 text-[#0094c8]"
                                }`}>
                                  {canReview ? (
                                    <Check className="w-5 h-5" />
                                  ) : (
                                    <Clipboard className="w-5 h-5" />
                                  )}
                                </div>
                                <div>
                                  <h5 className="text-[10px] font-black text-slate-400 uppercase tracking-widest font-mono">
                                    {canReview ? "Supplemental Disclosures" : "Position Description"}
                                  </h5>
                                  <h4 className="text-sm font-black text-[#0f2d4a] uppercase">
                                    {canReview ? "Supplemental Application Submission" : "Essential Role Details"}
                                  </h4>
                                </div>
                              </div>
                              <div>
                                {canReview ? (
                                  renderCoverLetterOrQA(app.coverLetter || "")
                                ) : (
                                  <div className="space-y-5">
                                    <PositionDetail
                                      text={
                                        matchingOpportunity?.description || ""
                                      }
                                    />

                                    {(matchingOpportunity?.qualifications ||
                                      matchingOpportunity?.skills) && (
                                      <div className="pt-5 border-t border-slate-100 flex flex-col gap-5">
                                        {matchingOpportunity?.qualifications && (
                                          <div className="space-y-3">
                                            <div className="flex items-center gap-2">
                                              <div className="w-1 h-3 rounded-full bg-[#0094c8]" />
                                              <h5 className="text-[10px] font-black text-[#0094c8] uppercase tracking-widest font-mono">
                                                Required Qualifications
                                              </h5>
                                            </div>
                                            <div className="text-slate-600 text-sm leading-relaxed pl-3 border-l-2 border-slate-100">
                                              {
                                                matchingOpportunity.qualifications
                                              }
                                            </div>
                                          </div>
                                        )}
                                        {matchingOpportunity?.skills && (
                                          <div className="space-y-3">
                                            <div className="flex items-center gap-2">
                                              <div className="w-1 h-3 rounded-full bg-[#0094c8]" />
                                              <h5 className="text-[10px] font-black text-[#0094c8] uppercase tracking-widest font-mono">
                                                Focus Skill Sets
                                              </h5>
                                            </div>
                                            <div className="text-slate-600 text-sm leading-relaxed pl-3 border-l-2 border-slate-100">
                                              {matchingOpportunity.skills}
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>

                            {(canReview || userRole === "advisor") && (
                              <CandidateProfile
                                studentId={app.studentId}
                                studentName={app.studentName}
                                studentEmail={app.studentEmail}
                              />
                            )}

                            {(userRole === "student" || userRole === "advisor") && app.coverLetter && (
                              <div className="bg-white border border-slate-200 rounded-3xl p-7 shadow-sm">
                                <div className="flex items-center gap-3 mb-5">
                                  <div className="w-10 h-10 rounded-2xl bg-emerald-50/40 text-emerald-600/80 border border-emerald-500/10 flex items-center justify-center">
                                    <Check className="w-5 h-5" />
                                  </div>
                                  <div>
                                    <h5 className="text-[10px] font-black text-slate-400 uppercase tracking-widest font-mono">
                                      Supplemental Disclosures
                                    </h5>
                                    <h4 className="text-sm font-black text-[#0f2d4a] uppercase">
                                      Supplemental Application Submission
                                    </h4>
                                  </div>
                                </div>
                                {renderCoverLetterOrQA(app.coverLetter)}
                              </div>
                            )}
                          </div>

                          <div className="lg:col-span-5 space-y-6">
                            {/* Action Card */}
                            <div className="bg-[#0f2d4a] rounded-3xl p-8 text-white shadow-xl relative overflow-hidden">
                              <div className="absolute top-0 right-0 w-32 h-32 bg-[#0094c8]/20 rounded-full blur-3xl -mr-16 -mt-16" />
                              <h5 className="text-[10px] font-black text-white/40 uppercase tracking-widest font-mono mb-6">
                                Engagement Center
                              </h5>

                              {app.status === "accepted" ? (
                                <div className="space-y-6 text-center py-4">
                                  <div className="w-20 h-20 bg-emerald-600/40 border border-emerald-500/20 text-emerald-200 rounded-3xl mx-auto flex items-center justify-center shadow-lg shadow-emerald-600/10 rotate-6">
                                    <Check className="w-10 h-10 text-emerald-300" />
                                  </div>
                                  <div>
                                    <h3 className="text-2xl font-black uppercase tracking-tight">
                                      Placement Active
                                    </h3>
                                    <p className="text-white/50 text-xs mt-2 font-mono uppercase tracking-widest">
                                      Hiring cycle complete
                                    </p>
                                  </div>
                                  {userRole === "student" && (
                                    <button
                                      onClick={() =>
                                        setCurrentView("placements")
                                      }
                                      className="w-full bg-white text-[#0f2d4a] py-4 rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-slate-50 transition-colors shadow-sm"
                                    >
                                      View Assignment Board
                                    </button>
                                  )}
                                </div>
                              ) : (
                                <div className="space-y-6">
                                  <div className="p-5 rounded-2xl bg-white/5 border border-white/10">
                                    <p className="text-xs text-white/80 leading-relaxed font-medium">
                                      {app.status === "rejected" ? (
                                        userRole === "student" ? (
                                          <div className="space-y-4 text-center py-2">
                                            <div className="w-12 h-12 bg-rose-500/20 rounded-2xl flex items-center justify-center mx-auto mb-2">
                                              <X className="w-6 h-6 text-rose-400" />
                                            </div>
                                            <p className="font-sans text-white/95 text-xs font-semibold leading-relaxed">
                                              This application cycle has been
                                              concluded.
                                            </p>
                                            {app.declineLetter && (
                                              <div className="bg-white/5 border border-white/10 p-4 rounded-xl text-left font-sans text-rose-200/90 whitespace-pre-wrap max-h-[150px] overflow-y-auto pr-1">
                                                {app.declineLetter}
                                              </div>
                                            )}
                                          </div>
                                        ) : (
                                          "Candidate recruitment closed. Feel free to re-evaluate or change selection above."
                                        )
                                      ) : userRole === "student" ? (
                                        "Your application is currently being reviewed by the partner organization's hiring committee. You will receive an alert once a decision is logged."
                                      ) : (
                                        "Examine the candidate's professional matrix and credentials before issuing a final recruitment decision below."
                                      )}
                                    </p>
                                  </div>

                                  {(userRole === "organization" ||
                                    userRole === "regional_admin" ||
                                    userRole === "superadmin") &&
                                    (app.status === "pending" ||
                                      app.status === "reviewed") && (
                                      <div className="flex items-center gap-1.5">
                                        <button
                                          onClick={() =>
                                            handleReview(app.id, "accepted")
                                          }
                                          className="p-3 bg-emerald-50 text-emerald-600 hover:bg-emerald-600 hover:text-white rounded-2xl transition-all shadow-sm active:scale-95 shadow-inner"
                                          title="Accept & Hire"
                                        >
                                          <Check className="w-5 h-5" />
                                        </button>
                                        <button
                                          onClick={() =>
                                            handleReview(app.id, "rejected")
                                          }
                                          className="p-3 bg-red-50 text-red-600 hover:bg-red-600 hover:text-white rounded-2xl transition-all shadow-sm active:scale-95 shadow-inner"
                                          title="Decline"
                                        >
                                          <X className="w-5 h-5" />
                                        </button>
                                      </div>
                                    )}
                                </div>
                              )}
                            </div>

                            {(() => {
                              const matchingPlacement = placements?.find(
                                (p) =>
                                  p.opportunityId === app.opportunityId &&
                                  (p.studentId === app.studentId ||
                                    (p.studentEmail &&
                                      app.studentEmail &&
                                      p.studentEmail.toLowerCase().trim() ===
                                        app.studentEmail.toLowerCase().trim()))
                              )

                              const timelineEvents = (() => {
                                const eventsList: Array<{
                                  title: string;
                                  subtitle?: string;
                                  dateStr?: string;
                                  timestamp: number;
                                  colorClass: string;
                                  icon: any;
                                }> = [];

                                const getEventTimestamp = (
                                  dateStr?: string,
                                  fallbackOffsetDays = 0,
                                ): number => {
                                  if (!dateStr)
                                    return (
                                      Date.now() + fallbackOffsetDays * 86400000
                                    )
                                  const t = Date.parse(dateStr)
                                  if (isNaN(t)) {
                                    return (
                                      Date.now() + fallbackOffsetDays * 86400000
                                    )
                                  }
                                  return t;
                                };

                                const formatEventDate = (dateStr?: string) => {
                                  if (!dateStr) return "";
                                  if (dateStr.includes(":") && !dateStr.includes("T") && isNaN(Date.parse(dateStr))) {
                                    const parts = dateStr.split(" ")
                                    if (parts.length >= 2) {
                                      return `${parts[0]} ${formatTime12HourEST(parts[1])}`;
                                    }
                                    return formatTime12HourEST(dateStr)
                                  }
                                  const parsed = new Date(dateStr)
                                  if (isNaN(parsed.getTime())) return dateStr;
                                  return (
                                    parsed.toLocaleString("en-US", {
                                      year: "numeric",
                                      month: "numeric",
                                      day: "numeric",
                                      hour: "numeric",
                                      minute: "2-digit",
                                      hour12: true,
                                      timeZone: "America/New_York",
                                    }) + " EST"
                                  )
                                };

                                // 1. Applied Date
                                if (app.appliedAt) {
                                  eventsList.push({
                                    title: "Applied Date",
                                    dateStr: formatEventDate(app.appliedAt),
                                    timestamp: getEventTimestamp(app.appliedAt),
                                    colorClass:
                                      "bg-blue-500 text-blue-500 border-blue-200",
                                    icon: FileText,
                                  })
                                }

                                // 2. Interview Date
                                const matchingInterview = interviews?.find(
                                  (i) => i.applicationId === app.id,
                                )
                                if (
                                  matchingInterview ||
                                  app.interviewScheduled
                                ) {
                                  const interviewDateStr = matchingInterview
                                    ? `${matchingInterview.date} ${formatTime12HourEST(matchingInterview.time)}`
                                    : "";
                                  eventsList.push({
                                    title: "Interview Date",
                                    dateStr: interviewDateStr
                                      ? formatEventDate(interviewDateStr)
                                      : "Pending",
                                    timestamp: getEventTimestamp(
                                      interviewDateStr || app.appliedAt,
                                      1,
                                    ),
                                    colorClass:
                                      "bg-indigo-500 text-indigo-500 border-indigo-200",
                                    icon: Calendar,
                                  })
                                }

                                // 3. Decision Date
                                if (
                                  app.status === "accepted" ||
                                  app.status === "rejected"
                                ) {
                                  eventsList.push({
                                    title: "Decision Date",
                                    dateStr: formatEventDate(
                                      app.decisionDate || matchingPlacement?.startDate || app.appliedAt
                                    ),
                                    timestamp: getEventTimestamp(
                                      app.decisionDate || matchingPlacement?.startDate || app.appliedAt,
                                      3,
                                    ),
                                    colorClass:
                                      app.status === "accepted" ? "bg-emerald-50/40 text-emerald-700/85 border-emerald-600/15" : "bg-rose-500 text-rose-500 border-rose-200",
                                    icon: app.status === "accepted" ? CheckCircle : XCircle,
                                  })
                                }

                                // 4. Evaluation Date
                                if (matchingPlacement?.eval1) {
                                  eventsList.push({
                                    title: "Evaluation Date",
                                    dateStr: matchingPlacement.eval1.submittedAt ? formatEventDate(matchingPlacement.eval1.submittedAt) : "Drafted",
                                    timestamp: getEventTimestamp(
                                      matchingPlacement.eval1.submittedAt || Date.now().toString(),
                                    ),
                                    colorClass:
                                      "bg-teal-500 text-teal-500 border-teal-200",
                                    icon: Award,
                                  })
                                }

                                // 5. Evaluation 2 Date
                                if (matchingPlacement?.eval2) {
                                  eventsList.push({
                                    title: "Evaluation 2 Date",
                                    dateStr: matchingPlacement.eval2.submittedAt ? formatEventDate(matchingPlacement.eval2.submittedAt) : "Drafted",
                                    timestamp: getEventTimestamp(
                                      matchingPlacement.eval2.submittedAt || Date.now().toString(),
                                    ),
                                    colorClass:
                                      "bg-teal-600 text-teal-600 border-teal-300",
                                    icon: Award,
                                  })
                                }

                                // 6. Completion Date
                                if (
                                  matchingPlacement?.status === "completed" ||
                                  matchingPlacement?.status === "terminated" ||
                                  matchingPlacement?.status === "hired"
                                ) {
                                  eventsList.push({
                                    title: "Completion Date",
                                    dateStr: formatEventDate(
                                      matchingPlacement.endDate || matchingPlacement.hiredDate,
                                    ),
                                    timestamp: getEventTimestamp(
                                      matchingPlacement.endDate || matchingPlacement.hiredDate,
                                    ),
                                    colorClass:
                                      "bg-[#0094c8] text-[#0094c8] border-[#0094c8]/20",
                                    icon: Sparkles,
                                  })
                                }


                                return eventsList.sort(
                                  (a, b) => a.timestamp - b.timestamp,
                                )
                              })()

                              return (
                                <div className="bg-white border border-slate-200 rounded-3xl p-5 shadow-xs">
                                  <h5 className="text-[10px] font-black text-slate-400 uppercase tracking-widest font-mono mb-4">
                                    Timeline Log
                                  </h5>
                                  <div className="relative pl-5 border-l border-slate-200 space-y-3">
                                    {timelineEvents.map((evt, idx) => {
                                      const IconComponent = evt.icon;
                                      return (
                                        <div
                                          key={idx}
                                          className="relative group pl-1"
                                        >
                                          {/* Timeline dot - perfectly centered on the left border line */}
                                          <div
                                            className={`absolute -left-[25px] top-[12px] w-2.5 h-2.5 rounded-full border-2 border-white ${evt.colorClass.split(" ")[0]} shadow-xs transition-all duration-300 group-hover:scale-110`}
                                          />

                                          <div className="bg-slate-50/50 hover:bg-slate-100/60 p-2.5 rounded-xl border border-slate-100 transition-all">
                                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
                                              <div className="flex items-center gap-1.5 flex-wrap">
                                                {IconComponent && (
                                                  <IconComponent
                                                    className={`w-3.5 h-3.5 ${evt.colorClass.split(" ")[1] || "text-[#0f2d4a]"}`}
                                                  />
                                                )}
                                                <span className="text-[10px] font-bold text-[#0f2d4a] uppercase tracking-wide">
                                                  {evt.title}
                                                </span>
                                              </div>
                                              {evt.dateStr && (
                                                <span className="text-[8px] font-mono font-medium text-slate-400">
                                                  {evt.dateStr}
                                                </span>
                                              )}
                                            </div>
                                            {evt.subtitle && (
                                              <p className="text-[10px] text-slate-500 mt-1 leading-relaxed font-normal">
                                                {evt.subtitle}
                                              </p>
                                            )}
                                          </div>
                                        </div>
                                        );
                                      })}
                                  </div>
                                </div>
                                );
                              })()}
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    )}
          {onboardingModal.show && (
            <div
              className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
              onClick={() => setOnboardingModal({ show: false, content: null })}
            >
              <div
                className="bg-white rounded-3xl p-8 max-w-lg w-full shadow-2xl space-y-4"
                onClick={(e) => e.stopPropagation()}
              >
                <h3 className="text-lg font-black uppercase text-[#0f2d4a]">
                  Onboarding Instructions
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed">
                  {onboardingModal.content}
                </p>
                <button
                  onClick={() =>
                    setOnboardingModal({ show: false, content: null })
                  }
                  className="w-full bg-[#0094c8] text-white py-3 rounded-xl font-bold"
                >
                  Close
                </button>
              </div>
            </div>
          )}

          {interviewModal.show && (
            <div
              className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
              onClick={() => {
                setInterviewModal({ show: false, app: null })
                setInterviewData({
                  date: "",
                  time: "",
                  person: "",
                  mode: "virtual",
                  link: "",
                  address: "",
                  phone: "",
                })
              }}
            >
              <div
                className="bg-white rounded-3xl p-8 max-w-lg w-full shadow-2xl space-y-6"
                onClick={(e) => e.stopPropagation()}
              >
                <div>
                  <h3 className="text-lg font-black uppercase text-[#0f2d4a]">
                    {interviewModal.app?.interviewScheduled
                      ? "Reschedule Interview"
                      : "Schedule Interview"}
                  </h3>
                  <p className="text-xs text-slate-500 mt-1">
                    {interviewModal.app?.interviewScheduled
                      ? "Update interview details for"
                      : "Set up an interview with"}{" "}
                    {interviewModal.app?.studentName}
                  </p>
                </div>

                <div className="space-y-4">
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-black tracking-wider uppercase text-slate-400">
                      Date
                    </label>
                    <input
                      type="date"
                      value={interviewData.date}
                      onChange={(e) =>
                        setInterviewData({
                          ...interviewData,
                          date: e.target.value,
                        })
                      }
                      className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl text-sm"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-black tracking-wider uppercase text-slate-400">
                      Time
                    </label>
                    <input
                      type="time"
                      value={interviewData.time}
                      onChange={(e) =>
                        setInterviewData({
                          ...interviewData,
                          time: e.target.value,
                        })
                      }
                      className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl text-sm"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-black tracking-wider uppercase text-slate-400">
                      Interviewer (Person)
                    </label>
                    <input
                      type="text"
                      value={interviewData.person}
                      onChange={(e) =>
                        setInterviewData({
                          ...interviewData,
                          person: e.target.value,
                        })
                      }
                      placeholder="Interviewer Name"
                      className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl text-sm"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-black tracking-wider uppercase text-slate-400">
                      Mode
                    </label>
                    <select
                      value={interviewData.mode}
                      onChange={(e) => {
                        const newMode = e.target.value;
                        const opp = interviewModal.app ? opportunities?.find(o => o.id === interviewModal.app.opportunityId) : null;
                        
                        setInterviewData(prev => {
                          const newData = { ...prev, mode: newMode as any };
                          if (newMode === 'in_person' && !prev.address) {
                            newData.address = opp?.address || activeUser?.address || '';
                          }
                          if (newMode === 'phone' && !prev.phone) {
                            newData.phone = activeUser?.contactPhone || '';
                          }
                          return newData;
                        })
                      }}
                      className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl text-sm"
                    >
                      <option value="virtual">Virtual</option>
                      <option value="in_person">In Person</option>
                      <option value="phone">Phone</option>
                    </select>
                  </div>
                  {interviewData.mode === "virtual" && (
                    <div className="space-y-1.5">
                      <label className="text-[10px] font-black tracking-wider uppercase text-slate-400">
                        Meeting Link
                      </label>
                      <input
                        type="url"
                        value={interviewData.link}
                        onChange={(e) =>
                          setInterviewData({
                            ...interviewData,
                            link: e.target.value,
                          })
                        }
                        placeholder="https://zoom.us/j/..."
                        className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl text-sm"
                      />
                    </div>
                  )}
                  {interviewData.mode === "in_person" && (
                    <div className="space-y-1.5">
                      <label className="text-[10px] font-black tracking-wider uppercase text-slate-400">
                        Address
                      </label>
                      <VerifiedAddressInput
                        id="interview-address"
                        value={interviewData.address}
                        onChange={(val) =>
                          setInterviewData({ ...interviewData, address: val })
                        }
                        placeholder="123 Main St..."
                        className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl text-sm"
                      />
                    </div>
                  )}
                  {interviewData.mode === "phone" && (
                    <div className="space-y-1.5">
                      <label className="text-[10px] font-black tracking-wider uppercase text-slate-400">
                        Phone Code / Number
                      </label>
                      <input
                        type="text"
                        value={interviewData.phone}
                        onChange={(e) =>
                          setInterviewData({
                            ...interviewData,
                            phone: formatPhoneNumber(e.target.value),
                          })
                        }
                        placeholder="555-0123"
                        className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl text-sm"
                      />
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-3 pt-2">
                  <button
                    onClick={() => {
                      setInterviewModal({ show: false, app: null })
                      setInterviewData({
                        date: "",
                        time: "",
                        person: "",
                        mode: "virtual",
                        link: "",
                        address: "",
                        phone: "",
                      })
                    }}
                    className="w-full bg-slate-100 hover:bg-slate-200 text-slate-700 py-3 rounded-xl font-bold transition-all"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleScheduleInterview}
                    disabled={
                      !interviewData.date ||
                      !interviewData.time ||
                      !interviewData.person
                    }
                    className="w-full bg-[#0094c8] hover:bg-[#007ba6] text-white py-3 rounded-xl font-bold transition-all disabled:opacity-50"
                  >
                    {interviewModal.app?.interviewScheduled
                      ? "Reschedule"
                      : "Schedule"}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
};

export { StudentApplications };
export default StudentApplications;
